import React, { useEffect, useMemo, useState, useCallback } from "react";
import { db } from "../firebase";
import {
  collection, deleteDoc, doc, onSnapshot, setDoc, updateDoc, getDoc,
  getDocs, writeBatch, serverTimestamp, addDoc, query, orderBy,
} from "firebase/firestore";
import ProgrammeAIImporter from "./ProgrammeAIImporter";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS  —  Dark Command-Centre Theme
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  // Core backgrounds
  bg:         "#0A0E1A",   // near-black navy
  bgPanel:    "#0F1422",   // panel base
  bgSurface:  "#141929",   // elevated surface
  bgHover:    "#1A2035",   // hover state
  bgCard:     "#111827",   // card surface

  // Borders
  border:     "#1E2A42",
  borderBright:"#2A3A5C",

  // Semantic colours
  blue:       "#3B82F6",
  blueDim:    "#1D3461",
  blueSoft:   "#1A2A4A",
  teal:       "#10B981",
  tealDim:    "#064E3B",
  tealSoft:   "#0D2E25",
  amber:      "#F59E0B",
  amberDim:   "#78350F",
  amberSoft:  "#2A1E08",
  red:        "#EF4444",
  redDim:     "#7F1D1D",
  redSoft:    "#2A1010",
  maroon:     "#F87171",    // critical accent (warm red on dark bg)
  maroonSoft: "#2D1515",
  purple:     "#A78BFA",
  purpleSoft: "#1E1635",
  orange:     "#FB923C",
  muted:      "#4B5A7A",
  mutedBright:"#6B7FA8",
  text:       "#E2E8F0",
  textDim:    "#94A3B8",
  white:      "#FFFFFF",

  // Radius
  r4:  "4px",
  r6:  "6px",
  r8:  "8px",
  r10: "10px",
  r12: "12px",
  r16: "16px",

  // Shadows
  shadowSm: "0 1px 3px rgba(0,0,0,0.4)",
  shadowMd: "0 4px 20px rgba(0,0,0,0.5)",
  shadowLg: "0 8px 40px rgba(0,0,0,0.6)",

  // Typography
  fontMono: "'JetBrains Mono','Fira Code','Cascadia Code',monospace",
  fontSans: "'DM Sans','Helvetica Neue',sans-serif",
};


// ─────────────────────────────────────────────────────────────────────────────
// PROGRAMME TYPE  —  classification palette
// ─────────────────────────────────────────────────────────────────────────────
const PROG_TYPE = {
  Working:     { color: T.teal,   bg: T.tealSoft,   border: T.tealDim,   label: "Working",     desc: "Day-to-day updated programme" },
  Contractual: { color: T.amber,  bg: T.amberSoft,  border: T.amberDim,  label: "Contractual", desc: "Agreed contract baseline" },
  Baseline:    { color: T.blue,   bg: T.blueSoft,   border: T.blueDim,   label: "Baseline",    desc: "Original accepted programme" },
  Revised:     { color: T.purple, bg: T.purpleSoft, border: T.purpleDim, label: "Revised",     desc: "Formally revised programme" },
  AsBuilt:     { color: T.orange, bg: "#1F1208",    border: "#5C3010",   label: "As-Built",    desc: "As-built record programme" },
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILITIES  (unchanged from original — no logic changes)
// ─────────────────────────────────────────────────────────────────────────────
const emptyProgrammeForm = {
  activityId: "", activityName: "", discipline: "", area: "",
  plannedStart: "", plannedFinish: "", durationDays: "",
  progress: "0", status: "Not Started",
  predecessor: "", successor: "", dependencyType: "FS", lagDays: "0",
  critical: false,
  baselineStart: "", baselineFinish: "", revisedStart: "", revisedFinish: "",
 actualStart: "", actualFinish: "",

outlineLevel: 1,

parentActivityId: "",

isSummaryTask: false,

childCount: 0,

lastDiaryId: "", lastDiaryDate: "", lastDiaryStatus: "", lastDiaryNote: "",

remarks: "",
};

function startOfDay(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}
function daysBetween(start, end) {
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}
function fmtDate(str) {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtDateShort(str) {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d.getTime())) return str;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short" });
}
function formatDateInput(date) {
  if (!date || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

// ─── Working-day calendar helpers ───────────────────────────────────────────
// Calendar config:
//   {
//     mode: "calendar" | "mon-fri" | "mon-sat",
//     holidays: ["YYYY-MM-DD", ...],       // individual non-working dates
//     nonWorkingWindows: [                 // named non-working ranges, e.g. Builders' Break
//       { id, label, start: "YYYY-MM-DD", end: "YYYY-MM-DD", color? }
//     ]
//   }
// Both holidays AND nonWorkingWindows are honoured in every mode, including
// "calendar" mode — a defined non-working date is never a working day.

// Build a Set of all blocked YYYY-MM-DD strings (holidays + every day inside
// every non-working window). Memoise-friendly: only depends on the cal object.
function buildBlockedDateSet(cal) {
  const set = new Set();
  if (!cal) return set;
  if (Array.isArray(cal.holidays)) cal.holidays.forEach((d) => d && set.add(d));
  if (Array.isArray(cal.nonWorkingWindows)) {
    cal.nonWorkingWindows.forEach((w) => {
      if (!w?.start || !w?.end) return;
      const s = startOfDay(w.start);
      const e = startOfDay(w.end);
      if (!s || !e || e < s) return;
      let d = new Date(s);
      while (d <= e) {
        set.add(formatDateInput(d));
        d = addDays(d, 1);
      }
    });
  }
  return set;
}

function isWorkingDay(date, cal, blockedSet) {
  // Weekly pattern check — calendar mode allows every day of the week
  if (cal && cal.mode !== "calendar") {
    const dow = date.getDay(); // 0=Sun, 6=Sat
    if (cal.mode === "mon-fri" && (dow === 0 || dow === 6)) return false;
    if (cal.mode === "mon-sat" && dow === 0) return false;
  }
  // Explicit non-working date check — applies in EVERY mode
  const set = blockedSet || buildBlockedDateSet(cal);
  if (set.has(formatDateInput(date))) return false;
  return true;
}

// Add `n` working days to a date. Always lands on a working day. Skips
// weekends (per mode), individual holidays, AND non-working windows.
// Hard safety cap on iterations so a misconfigured infinite window (e.g.
// 7-day-blocked + every weekend blocked) can't lock the UI.
function addWorkingDays(date, n, cal) {
  // Calendar mode with no holidays/windows is the cheap path
  const set = buildBlockedDateSet(cal);
  const hasCalendarConstraints = (cal && cal.mode !== "calendar") || set.size > 0;
  if (!hasCalendarConstraints) return addDays(date, n);

  const MAX_ITER = 10000; // ~27 years of daily iteration — generous safety net
  let d = new Date(date);
  let iter = 0;
  // Snap start forward to a working day if it isn't one
  while (!isWorkingDay(d, cal, set)) {
    d = addDays(d, 1);
    if (++iter > MAX_ITER) return d;
  }
  let remaining = n;
  while (remaining > 0) {
    d = addDays(d, 1);
    if (++iter > MAX_ITER) return d;
    if (isWorkingDay(d, cal, set)) remaining--;
  }
  return d;
}

// Count working days between two dates inclusive
function workingDaysBetweenInclusive(start, end, cal) {
  if (!start || !end) return 0;
  const set = buildBlockedDateSet(cal);
  const hasCalendarConstraints = (cal && cal.mode !== "calendar") || set.size > 0;
  if (!hasCalendarConstraints) return Math.max(0, daysBetween(start, end) + 1);
  if (end < start) return 0;
  let count = 0;
  let d = new Date(start);
  const MAX_ITER = 10000;
  let iter = 0;
  while (d <= end) {
    if (isWorkingDay(d, cal, set)) count++;
    d = addDays(d, 1);
    if (++iter > MAX_ITER) break;
  }
  return count;
}

function getActivityDurationDays(activity) {
  const explicit = Number(activity.durationDays || 0);
  if (explicit > 0) return explicit;
  const s = startOfDay(activity.plannedStart);
  const f = startOfDay(activity.plannedFinish);
  if (!s || !f) return 1;
  return Math.max(1, daysBetween(s, f) + 1);
}

function getOutlineLevel(activity) {
  const explicit = Number(activity.outlineLevel || activity._outlineLevel || 0);
  if (explicit > 0) return explicit;

  const wbs = String(activity.activityId || "").trim();
  if (wbs.includes(".")) {
    return Math.max(1, wbs.split(".").filter(Boolean).length);
  }

  return 1;
}

function sortActivitiesByWbs(list) {
  return [...list].sort((a, b) =>
    String(a.activityId || a.id || "").localeCompare(
      String(b.activityId || b.id || ""),
      undefined,
      { numeric: true }
    )
  );
}

function inferHierarchyParent(activity, orderedActivities) {
  const currentLevel = getOutlineLevel(activity);
  if (currentLevel <= 1) return "";

  const currentKey = activity.id || activity.activityId;
  const idx = orderedActivities.findIndex((a) => (a.id || a.activityId) === currentKey);
  if (idx <= 0) return "";

  for (let i = idx - 1; i >= 0; i--) {
    const candidate = orderedActivities[i];
    if (getOutlineLevel(candidate) < currentLevel) {
      return String(candidate.id || candidate.activityId || "");
    }
  }

  return "";
}

function getHierarchyParentId(activity, orderedActivities) {
  return String(activity.parentActivityId || inferHierarchyParent(activity, orderedActivities) || "");
}

function applyHierarchyRollups(activities) {
  const ordered = sortActivitiesByWbs(activities);
  const childrenByParent = {};

  ordered.forEach((activity) => {
    const parentId = getHierarchyParentId(activity, ordered);
    if (!parentId) return;
    if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
    childrenByParent[parentId].push(activity);
  });

  const byId = {};
  ordered.forEach((activity) => {
    const id = String(activity.id || activity.activityId || "");
    byId[id] = {
      ...activity,
      outlineLevel: getOutlineLevel(activity),
      parentActivityId: getHierarchyParentId(activity, ordered),
    };
  });

  const rollup = (id) => {
    const node = byId[id];
    if (!node) return null;

    const children = childrenByParent[id] || [];

    if (!children.length) {
      return {
        ...node,
        isSummaryTask: false,
        childCount: 0,
      };
    }

    const rolledChildren = children
      .map((child) => rollup(String(child.id || child.activityId || "")))
      .filter(Boolean);

    const plannedStarts = rolledChildren.map((child) => child.plannedStart).filter(Boolean).sort();
    const plannedFinishes = rolledChildren.map((child) => child.plannedFinish).filter(Boolean).sort();
    const revisedStarts = rolledChildren.map((child) => child.revisedStart || child.plannedStart).filter(Boolean).sort();
    const revisedFinishes = rolledChildren.map((child) => child.revisedFinish || child.plannedFinish).filter(Boolean).sort();
    const actualStarts = rolledChildren.map((child) => child.actualStart).filter(Boolean).sort();
    const actualFinishes = rolledChildren.map((child) => child.actualFinish).filter(Boolean).sort();

    const progressValues = rolledChildren
      .map((child) => Number(child.progress || 0))
      .filter((value) => Number.isFinite(value));

    const plannedStart = plannedStarts[0] || node.plannedStart || "";
    const plannedFinish = plannedFinishes[plannedFinishes.length - 1] || node.plannedFinish || "";
    const revisedStart = revisedStarts[0] || node.revisedStart || plannedStart;
    const revisedFinish = revisedFinishes[revisedFinishes.length - 1] || node.revisedFinish || plannedFinish;
    const actualStart = actualStarts[0] || node.actualStart || "";
    const actualFinish = actualFinishes[actualFinishes.length - 1] || node.actualFinish || "";

    const durationDays = plannedStart && plannedFinish
      ? String(Math.max(1, daysBetween(startOfDay(plannedStart), startOfDay(plannedFinish)) + 1))
      : String(node.durationDays || "");

    return {
      ...node,
      plannedStart,
      plannedFinish,
      revisedStart,
      revisedFinish,
      actualStart,
      actualFinish,
      durationDays,
      progress: progressValues.length
        ? String(Math.round(progressValues.reduce((sum, value) => sum + value, 0) / progressValues.length))
        : String(node.progress || "0"),
      isSummaryTask: true,
      childCount: rolledChildren.length,
    };
  };

  Object.keys(byId).forEach((id) => {
    byId[id] = rollup(id);
  });

  return ordered.map((activity) => byId[String(activity.id || activity.activityId || "")] || activity);
}


// Parse comma/space-separated predecessor list, e.g. "A100, A105 ; A110"
function parsePredecessorIds(raw) {
  if (!raw) return [];
  return String(raw)
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Recompute revised dates for an activity given ALL its predecessors (FS only here).
// Uses working-day calendar if provided. MS-Project convention: Start = max(predFinish + lag).
function applyDependencyToActivity(activity, activityMap, cal) {
  const predIds = parsePredecessorIds(activity.predecessor);
  if (!predIds.length || activity.dependencyType !== "FS") return { ...activity };
  const lagDays = Number(activity.lagDays || 0);
  const duration = getActivityDurationDays(activity);
  let driverFinish = null;
  predIds.forEach((pid) => {
    const p = activityMap[pid];
    if (!p) return;
    const pf = startOfDay(p.revisedFinish || p.plannedFinish);
    if (!pf) return;
    if (!driverFinish || pf > driverFinish) driverFinish = pf;
  });
  if (!driverFinish) return { ...activity };
  // MS Project: successor starts the next working day after predecessor finish + lag.
  // We treat "finish on day N" + lag working days = start on day N + lag + 1 (next working day).
  const revisedStart = addWorkingDays(driverFinish, lagDays + 1, cal);
  const revisedFinish = addWorkingDays(revisedStart, duration - 1, cal);
  return { ...activity, revisedStart: formatDateInput(revisedStart), revisedFinish: formatDateInput(revisedFinish) };
}
function isActivityDelayed(activity) {
  if (activity.status === "Delayed") return true;
  if (!activity.plannedFinish || !activity.revisedFinish) return false;
  return activity.revisedFinish > activity.plannedFinish;
}
function getDaysBehind(activity) {
  if (!activity.plannedFinish || !activity.revisedFinish) return 0;
  const planned = startOfDay(activity.plannedFinish);
  const revised = startOfDay(activity.revisedFinish);
  if (!planned || !revised) return 0;
  const diff = Math.round((revised.getTime() - planned.getTime()) / 86400000);
  return diff > 0 ? diff : 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// CPM  —  MS-Project-aligned forward/backward pass
//   • Multiple predecessors per activity (comma-separated list in `predecessor`)
//   • Lag applied per-predecessor edge (no spurious +1 buffer)
//   • Critical threshold is configurable (default 0, matching MS Project)
//   • Working-day calendar handled at the dates layer (revised dates),
//     while ES/EF/LS/LF here are reported in DURATION units so float math
//     stays consistent regardless of calendar.
// ─────────────────────────────────────────────────────────────────────────────
function computeCPM(activities, options = {}) {
  const criticalThreshold = Number(options.criticalThreshold ?? 0);
  if (!activities.length) return [];

  const nodeMap = {};
  activities.forEach((a) => {
    const id = a.activityId || a.id;
    const dur = getActivityDurationDays(a);
    nodeMap[id] = {
      ...a, _id: id, _dur: dur,
      ES: 0, EF: 0, LS: 0, LF: 0, TF: 0, FF: 0,
      isCPM: false,
      predIds: parsePredecessorIds(a.predecessor),
      succIds: [],
    };
  });

  // Build successor adjacency from predecessor lists + explicit successor field
  Object.values(nodeMap).forEach((node) => {
    node.predIds.forEach((pid) => {
      if (nodeMap[pid] && !nodeMap[pid].succIds.includes(node._id)) {
        nodeMap[pid].succIds.push(node._id);
      }
    });
    if (node.successor && nodeMap[node.successor]) {
      if (!nodeMap[node.successor].predIds.includes(node._id)) {
        nodeMap[node.successor].predIds.push(node._id);
      }
      if (!node.succIds.includes(node.successor)) node.succIds.push(node.successor);
    }
  });

  // Topological sort (Kahn). Nodes that participate in a cycle simply won't appear.
  const inDegree = {};
  Object.keys(nodeMap).forEach((k) => { inDegree[k] = nodeMap[k].predIds.filter((pid) => nodeMap[pid]).length; });
  const queue = Object.keys(nodeMap).filter((k) => inDegree[k] === 0);
  const topo = [];
  while (queue.length) {
    const cur = queue.shift();
    topo.push(cur);
    (nodeMap[cur]?.succIds || []).forEach((sid) => {
      if (nodeMap[sid]) {
        inDegree[sid]--;
        if (inDegree[sid] === 0) queue.push(sid);
      }
    });
  }

  // Forward pass — ES/EF in duration units.
  // Lag is a property of the successor in this data model, so we add it once to
  // the driving predecessor's EF when computing this node's ES.
  topo.forEach((id) => {
    const node = nodeMap[id];
    const lag = Number(node.lagDays || 0);
    if (!node.predIds.length) {
      node.ES = 0;
    } else {
      const candidates = node.predIds
        .map((pid) => (nodeMap[pid] ? nodeMap[pid].EF + lag : null))
        .filter((v) => v !== null);
      node.ES = candidates.length ? Math.max(...candidates) : 0;
    }
    node.EF = node.ES + node._dur;
  });

  const projectDuration = Object.values(nodeMap).length
    ? Math.max(...Object.values(nodeMap).map((n) => n.EF))
    : 0;

  // Backward pass
  [...topo].reverse().forEach((id) => {
    const node = nodeMap[id];
    if (!node.succIds.length) {
      node.LF = projectDuration;
    } else {
      const candidates = node.succIds
        .map((sid) => {
          if (!nodeMap[sid]) return projectDuration;
          const sLag = Number(nodeMap[sid].lagDays || 0);
          return nodeMap[sid].LS - sLag;
        });
      node.LF = candidates.length ? Math.min(...candidates) : projectDuration;
    }
    node.LS = node.LF - node._dur;
    node.TF = node.LF - node.EF;
  });

  // Free float and critical flag
  topo.forEach((id) => {
    const node = nodeMap[id];
    if (!node.succIds.length) {
      node.FF = Math.max(0, node.LF - node.EF);
    } else {
      const candidates = node.succIds
        .map((sid) => (nodeMap[sid] ? nodeMap[sid].ES - node.EF - Number(nodeMap[sid].lagDays || 0) : 0));
      node.FF = candidates.length ? Math.max(0, Math.min(...candidates)) : 0;
    }
    node.isCPM = node.TF <= criticalThreshold;
  });

  return Object.values(nodeMap);
}

function computeEVM(activities) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let PV = 0, EV = 0, AC = 0, BAC = 0;
  activities.forEach((a) => {
    const dur = getActivityDurationDays(a); BAC += dur;
    const ps = startOfDay(a.plannedStart); const pf = startOfDay(a.plannedFinish);
    if (!ps || !pf) return;
    if (today >= pf) PV += dur;
    else if (today > ps) PV += (daysBetween(ps, today) / dur) * dur;
    const pct = Math.min(100, Math.max(0, Number(a.progress || 0)));
    EV += (pct / 100) * dur;
    AC += (pct / 100) * dur * 1.05;
  });
  const SPI = PV > 0 ? EV / PV : 1;
  const CPI = AC > 0 ? EV / AC : 1;
  return { SPI: Math.round(SPI * 100) / 100, CPI: Math.round(CPI * 100) / 100, PV, EV, BAC };
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────

function Badge({ children, variant = "default", size = "sm" }) {
  const map = {
    default:  { bg: T.bgSurface, color: T.mutedBright, border: T.border },
    info:     { bg: T.blueSoft,  color: T.blue,        border: T.blueDim },
    success:  { bg: T.tealSoft,  color: T.teal,        border: T.tealDim },
    warning:  { bg: T.amberSoft, color: T.amber,       border: T.amberDim },
    danger:   { bg: T.redSoft,   color: T.red,         border: T.redDim },
    critical: { bg: T.maroonSoft,color: T.maroon,      border: T.redDim },
    purple:   { bg: T.purpleSoft,color: T.purple,      border: "#3B2D6A" },
    orange:   { bg: "#1F1208",   color: T.orange,      border: "#5C3010" },
  };
  const s = map[variant] || map.default;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: size === "xs" ? "1px 6px" : "2px 8px",
      borderRadius: 999,
      fontSize: size === "xs" ? 10 : 11,
      fontWeight: 700, letterSpacing: "0.02em",
      background: s.bg, color: s.color,
      border: `1px solid ${s.border}`,
      whiteSpace: "nowrap", fontFamily: T.fontSans,
    }}>{children}</span>
  );
}

function Btn({ children, onClick, variant = "primary", size = "md", disabled, type = "button" }) {
  const [hover, setHover] = useState(false);
  const base = {
    border: "none", borderRadius: T.r8, fontWeight: 600, cursor: "pointer",
    fontFamily: T.fontSans, transition: "all 0.15s", lineHeight: 1,
    padding: size === "sm" ? "6px 12px" : size === "xs" ? "4px 8px" : "9px 18px",
    fontSize: size === "sm" ? 12 : size === "xs" ? 11 : 13,
    opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? "none" : "auto",
    display: "inline-flex", alignItems: "center", gap: 6,
  };
  const variants = {
    primary:   { background: hover ? "#2563EB" : T.blue,       color: "#fff" },
    secondary: { background: hover ? T.bgHover : T.bgSurface,  color: T.text, border: `1px solid ${T.border}` },
    danger:    { background: hover ? "#DC2626" : T.red,         color: "#fff" },
    ghost:     { background: hover ? T.bgHover : "transparent", color: T.blue, border: `1px solid ${T.border}` },
    purple:    { background: hover ? "#7C3AED" : T.purple,      color: "#fff" },
    teal:      { background: hover ? "#059669" : T.teal,        color: "#fff" },
    subtle:    { background: hover ? T.bgHover : "transparent", color: T.mutedBright, border: `1px solid transparent` },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ ...base, ...(variants[variant] || variants.primary) }}>
      {children}
    </button>
  );
}

function ProgressBar({ value, status, showLabel = true, height = 5 }) {
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  const color = status === "Completed" ? T.teal : status === "Delayed" ? T.red : status === "In Progress" ? T.blue : T.muted;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height, background: T.bgSurface, borderRadius: 999, overflow: "hidden", border: `1px solid ${T.border}` }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 999, transition: "width 0.3s" }} />
      </div>
      {showLabel && <span style={{ fontSize: 10, fontWeight: 700, color, minWidth: 28, fontFamily: T.fontMono }}>{pct}%</span>}
    </div>
  );
}

function FloatPill({ totalFloat }) {
  if (totalFloat === undefined || totalFloat === null) return <span style={{ color: T.muted, fontSize: 11 }}>—</span>;
  const tf = Number(totalFloat);
  const color  = tf === 0 ? T.maroon : tf <= 5 ? T.red : tf <= 14 ? T.amber : T.teal;
  const bg     = tf === 0 ? T.maroonSoft : tf <= 5 ? T.redSoft : tf <= 14 ? T.amberSoft : T.tealSoft;
  const border = tf === 0 ? T.redDim    : tf <= 5 ? "#5C1A1A"  : tf <= 14 ? T.amberDim  : T.tealDim;
  const label  = tf === 0 ? "CRIT" : `${tf}d`;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "2px 7px",
      borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.04em",
      background: bg, color, border: `1px solid ${border}`,
      fontFamily: T.fontMono,
    }}>{label}</span>
  );
}

function statusBadgeVariant(status) {
  if (status === "Completed")   return "success";
  if (status === "In Progress") return "info";
  if (status === "Delayed")     return "danger";
  return "default";
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM INPUT STYLES
// ─────────────────────────────────────────────────────────────────────────────
const inp = {
  width: "100%", boxSizing: "border-box",
  border: `1px solid ${T.border}`, borderRadius: T.r8,
  padding: "8px 10px", fontSize: 13, color: T.text,
  background: T.bgSurface, fontFamily: T.fontSans,
  outline: "none",
  transition: "border-color 0.15s",
};
const lbl = {
  display: "block", marginBottom: 4, fontSize: 10, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.08em", color: T.muted,
  fontFamily: T.fontSans,
};

// ─────────────────────────────────────────────────────────────────────────────
// COMMAND HEADER — KPI STRIP
// ─────────────────────────────────────────────────────────────────────────────
function CommandHeader({ summary, evm, activeVersion }) {
  const score = useMemo(() => {
    let s = 100;
    if (summary.total === 0) return 0;
    s -= Math.min(40, (summary.delayed / summary.total) * 100 * 0.5);
    if (evm.SPI < 0.8) s -= 20; else if (evm.SPI < 0.95) s -= 10;
    s -= Math.min(20, (summary.criticalDelayed || 0) * 10);
    if (summary.avgProgress < 10 && summary.inProgress > 0) s -= 5;
    return Math.max(0, Math.round(s));
  }, [summary, evm]);

  const healthColor = score >= 80 ? T.teal : score >= 60 ? T.amber : T.red;
  const healthLabel = score >= 80 ? "HEALTHY" : score >= 60 ? "AT RISK" : "CRITICAL";

  const spiColor = evm.SPI >= 0.95 ? T.teal : evm.SPI >= 0.8 ? T.amber : T.red;
  const svVal = Math.round(evm.EV - evm.PV);

  const kpis = [
    { label: "TOTAL", value: summary.total,       color: T.text,   sub: "activities" },
    { label: "IN PROGRESS", value: summary.inProgress, color: T.blue, sub: `${summary.notStarted} not started` },
    { label: "COMPLETED", value: summary.completed, color: T.teal, sub: summary.total ? `${Math.round((summary.completed / summary.total) * 100)}% of total` : "—" },
    { label: "DELAYED",   value: summary.delayed,  color: summary.delayed > 0 ? T.red : T.teal,
      sub: summary.critDelayed > 0 ? `${summary.critDelayed} on critical path!` : summary.delayed > 0 ? "needs review" : "on track",
      subColor: summary.critDelayed > 0 ? T.red : undefined },
    { label: "CPM CRITICAL", value: summary.cpmCritical, color: summary.cpmCritical > 0 ? T.maroon : T.teal, sub: "zero float" },
    { label: "NEAR-CRITICAL", value: summary.nearCritical, color: summary.nearCritical > 0 ? T.amber : T.teal, sub: "≤5 days float" },
    { label: "AVG PROGRESS", value: `${summary.avgProgress}%`, color: T.blue, sub: null },
    { label: "SPI", value: evm.SPI, color: spiColor, sub: evm.SPI >= 0.95 ? "on schedule" : evm.SPI >= 0.8 ? "slipping" : "behind" },
    { label: "SCH. VARIANCE", value: svVal === 0 ? "—" : `${svVal > 0 ? "+" : ""}${svVal}d`, color: svVal >= 0 ? T.teal : T.red, sub: "EV vs PV" },
  ];

  return (
    <div style={{
      background: T.bgPanel, borderBottom: `1px solid ${T.border}`,
      padding: "0 0 0 0", marginBottom: 12,
    }}>
      {/* Top header bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 16,
        padding: "10px 20px", borderBottom: `1px solid ${T.border}`,
        background: T.bg,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: "50%", background: healthColor,
            boxShadow: `0 0 8px ${healthColor}`,
          }} />
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", color: healthColor, fontFamily: T.fontSans }}>
            {healthLabel}
          </span>
          <span style={{ fontSize: 11, color: T.muted, marginLeft: 4, fontFamily: T.fontSans }}>
            Health Score: <span style={{ color: healthColor, fontWeight: 700, fontFamily: T.fontMono }}>{score}</span>
          </span>
        </div>
        <div style={{ width: 1, height: 18, background: T.border }} />
        {activeVersion ? (() => {
          const vts = PROG_TYPE[activeVersion.programmeType] || PROG_TYPE.Working;
          return (
            <>
              <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 10px", borderRadius: 999,
                background: vts.bg, color: vts.color, border: `1px solid ${vts.border}`,
                letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: T.fontSans }}>
                {vts.label} Programme
              </span>
              <span style={{ fontSize: 11, color: T.muted, fontFamily: T.fontSans, maxWidth: 260,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {activeVersion.label}
              </span>
            </>
          );
        })() : (
          <span style={{ fontSize: 11, color: T.muted, fontFamily: T.fontSans, letterSpacing: "0.05em" }}>
            PROGRAMME ANALYTICS
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.teal, animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 10, color: T.mutedBright, fontFamily: T.fontMono }}>LIVE</span>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(${kpis.length}, 1fr)`,
        borderBottom: `1px solid ${T.border}`,
      }}>
        {kpis.map((k, i) => (
          <div key={k.label} style={{
            padding: "12px 16px",
            borderRight: i < kpis.length - 1 ? `1px solid ${T.border}` : "none",
            background: T.bgPanel,
            transition: "background 0.15s",
          }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: T.muted, marginBottom: 4, fontFamily: T.fontSans }}>
              {k.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1, fontFamily: T.fontMono, letterSpacing: "-0.02em" }}>
              {k.value}
            </div>
            {k.sub && (
              <div style={{ fontSize: 10, color: k.subColor || T.muted, marginTop: 3, fontFamily: T.fontSans }}>
                {k.sub}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ALERT BANNER
// ─────────────────────────────────────────────────────────────────────────────
function AlertStrip({ summary }) {
  if (summary.critDelayed === 0 && summary.delayed === 0 && summary.nearCritical === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
      {summary.critDelayed > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "9px 16px", borderRadius: T.r8,
          background: T.maroonSoft, border: `1px solid ${T.redDim}`,
          borderLeft: `3px solid ${T.maroon}`,
        }}>
          <span style={{ fontSize: 14 }}>🚨</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.maroon, fontFamily: T.fontSans }}>
            CRITICAL PATH RISK: {summary.critDelayed} critical activit{summary.critDelayed !== 1 ? "ies" : "y"} delayed — project completion date is at risk
          </span>
        </div>
      )}
      {summary.delayed > 0 && summary.critDelayed === 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 16px", borderRadius: T.r8,
          background: T.redSoft, border: `1px solid ${T.redDim}`,
          borderLeft: `3px solid ${T.red}`,
        }}>
          <span style={{ fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.red, fontFamily: T.fontSans }}>
            {summary.delayed} activit{summary.delayed !== 1 ? "ies" : "y"} delayed (non-critical) — monitor float consumption
          </span>
        </div>
      )}
      {summary.nearCritical > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 16px", borderRadius: T.r8,
          background: T.amberSoft, border: `1px solid ${T.amberDim}`,
          borderLeft: `3px solid ${T.amber}`,
        }}>
          <span style={{ fontSize: 13 }}>⏱</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.amber, fontFamily: T.fontSans }}>
            {summary.nearCritical} near-critical activit{summary.nearCritical !== 1 ? "ies" : "y"} (≤5d float) — any delay will move these onto the critical path
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GANTT CHART
// ─────────────────────────────────────────────────────────────────────────────
function GanttChart({ ganttData }) {
  const { chartStart, chartEnd, totalDays, rows } = ganttData;

  const monthMarkers = useMemo(() => {
    const markers = [];
    if (!chartStart || !chartEnd || !totalDays) return markers;
    const cursor = new Date(chartStart); cursor.setDate(1);
    while (cursor <= chartEnd) {
      const offset = daysBetween(chartStart, cursor);
      const pct = (offset / totalDays) * 100;
      if (pct >= 0 && pct <= 100) markers.push({ label: cursor.toLocaleDateString("en-ZA", { month: "short", year: "2-digit" }), pct });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return markers;
  }, [chartStart, chartEnd, totalDays]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayPct = totalDays > 0 && chartStart ? Math.max(0, Math.min(100, (daysBetween(chartStart, today) / totalDays) * 100)) : null;
  const todayVisible = chartStart && chartEnd ? today >= chartStart && today <= chartEnd : false;

  if (!totalDays || !rows.length) {
    return <EmptyState message="No valid programme dates — add activities with dates to view the Gantt chart." />;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: 900 }}>
        {/* Legend */}
        <div style={{ display: "flex", gap: 20, marginBottom: 10, fontSize: 11, color: T.mutedBright }}>
          {[["Planned", T.blue], ["Revised", T.red], ["Baseline", "#4B5A7A"], ["Critical", T.maroon]].map(([l, c]) => (
            <span key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ display: "inline-block", width: 12, height: 5, background: c, borderRadius: 99 }} />{l}
            </span>
          ))}
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ display: "inline-block", width: 2, height: 14, background: T.amber }} />Today
          </span>
          <span style={{ marginLeft: "auto", fontFamily: T.fontMono, fontSize: 10, color: T.muted }}>
            {chartStart?.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })} → {chartEnd?.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}
          </span>
        </div>

        {/* Column headers + time axis */}
        <div style={{ display: "grid", gridTemplateColumns: "50px 60px 220px 1fr", gap: 6, marginBottom: 2 }}>
          {["WBS", "FLOAT", "ACTIVITY", ""].map((h) => (
            <div key={h} style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: T.muted, paddingLeft: 2 }}>{h}</div>
          ))}
        </div>

        {/* Month axis */}
        <div style={{ display: "grid", gridTemplateColumns: "50px 60px 220px 1fr", gap: 6, marginBottom: 6 }}>
          <div /><div /><div />
          <div style={{ position: "relative", height: 18, background: T.bg, borderRadius: T.r4, overflow: "hidden", border: `1px solid ${T.border}` }}>
            {monthMarkers.map((m) => (
              <div key={m.label} style={{ position: "absolute", left: `${m.pct}%`, top: 0, bottom: 0, paddingLeft: 5, fontSize: 9, fontWeight: 600, color: T.muted, display: "flex", alignItems: "center", borderLeft: `1px solid ${T.border}` }}>{m.label}</div>
            ))}
          </div>
        </div>

        {/* Rows */}
        {rows.map((row) => (
          <div key={row.id} style={{ display: "grid", gridTemplateColumns: "50px 60px 220px 1fr", gap: 6, marginBottom: 5, alignItems: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: row.isCritical ? T.maroon : T.blue, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.fontMono }} title={row.wbs}>{row.wbs || "—"}</div>
            <div><FloatPill totalFloat={row.totalFloat} /></div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.label}>
              {row.label}
              {row.isCritical && <span style={{ marginLeft: 5, fontSize: 9, background: T.maroon, color: "#fff", padding: "1px 4px", borderRadius: 99, fontFamily: T.fontSans }}>CP</span>}
            </div>

            <div style={{ position: "relative", height: 28, background: T.bg, borderRadius: T.r6, border: `1px solid ${row.isCritical ? T.redDim : T.border}`, overflow: "hidden" }}>
              {monthMarkers.map((m) => (
                <div key={m.label} style={{ position: "absolute", left: `${m.pct}%`, top: 0, bottom: 0, width: 1, background: T.border, pointerEvents: "none" }} />
              ))}
              {todayVisible && todayPct !== null && (
                <div style={{ position: "absolute", left: `${todayPct}%`, top: 0, bottom: 0, width: 2, background: T.amber, zIndex: 4, opacity: 0.9 }} />
              )}
              {row.floatDuration > 0 && row.plannedDuration > 0 && (
                <div style={{ position: "absolute", left: `${((row.plannedOffset + row.plannedDuration) / totalDays) * 100}%`, width: `${(row.floatDuration / totalDays) * 100}%`, top: 13, height: 2, background: T.amber, opacity: 0.5, borderRadius: 999, zIndex: 1 }} />
              )}
              {row.baselineDuration > 0 && (
                <div style={{ position: "absolute", left: `${(row.baselineOffset / totalDays) * 100}%`, width: `${(row.baselineDuration / totalDays) * 100}%`, top: 14, height: 4, background: T.muted, borderRadius: 999, zIndex: 1, opacity: 0.6 }} />
              )}
              {row.plannedDuration > 0 && (
                <div style={{ position: "absolute", left: `${(row.plannedOffset / totalDays) * 100}%`, width: `${(row.plannedDuration / totalDays) * 100}%`, top: 5, height: 9, background: row.isCritical ? T.maroon : T.blue, borderRadius: 999, zIndex: 2, overflow: "hidden" }}>
                  {row.progress > 0 && <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, row.progress)}%`, background: "rgba(255,255,255,0.25)", borderRadius: 999 }} />}
                </div>
              )}
              {row.revisedDuration > 0 && (
                <div style={{ position: "absolute", left: `${(row.revisedOffset / totalDays) * 100}%`, width: `${(row.revisedDuration / totalDays) * 100}%`, bottom: 5, height: 6, background: T.red, borderRadius: 999, zIndex: 3, opacity: 0.85 }} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// S-CURVE CHART
// ─────────────────────────────────────────────────────────────────────────────
function SCurveChart({ activities }) {
  const WIDTH = 680, HEIGHT = 200, PAD_L = 36, PAD_B = 28, PAD_T = 12, PAD_R = 12;
  const W = WIDTH - PAD_L - PAD_R;
  const H = HEIGHT - PAD_B - PAD_T;

  const data = useMemo(() => {
    if (!activities.length) return null;
    const allDates = activities.flatMap((a) => [startOfDay(a.plannedStart), startOfDay(a.plannedFinish)]).filter(Boolean);
    if (!allDates.length) return null;
    const start = new Date(Math.min(...allDates.map((d) => d.getTime())));
    const end   = new Date(Math.max(...allDates.map((d) => d.getTime())));
    const span  = daysBetween(start, end) || 1;
    const totalWeight = activities.reduce((s, a) => s + getActivityDurationDays(a), 0) || 1;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const points = [];
    for (let week = 0; week <= Math.ceil(span / 7); week++) {
      const d = addDays(start, week * 7);
      if (d > addDays(end, 7)) break;
      let planned = 0, actual = 0;
      activities.forEach((a) => {
        const ps = startOfDay(a.plannedStart); const pf = startOfDay(a.plannedFinish);
        const dur = getActivityDurationDays(a); const weight = dur / totalWeight;
        if (!ps || !pf) return;
        if (d >= pf) planned += weight; else if (d > ps) planned += weight * (daysBetween(ps, d) / dur);
        if (d <= today) {
          const pct = Math.min(100, Math.max(0, Number(a.progress || 0))) / 100;
          if (d >= pf) actual += weight * pct; else if (d > ps) actual += weight * Math.min(pct, daysBetween(ps, d) / dur);
        }
      });
      const x = (daysBetween(start, d) / span) * W;
      points.push({ x: PAD_L + Math.min(x, W), planned: planned * 100, actual: actual * 100, d });
    }
    return { points, start, end, span };
  }, [activities]);

  if (!data || data.points.length < 2) return <EmptyState message="Add planned dates to activities to view the S-Curve." />;

  const { points } = data;
  const toY = (pct) => PAD_T + H - (pct / 100) * H;
  const plannedPath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${toY(p.planned).toFixed(1)}`).join(" ");
  const actualPoints = points.filter((p) => p.actual > 0);
  const actualPath = actualPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${toY(p.actual).toFixed(1)}`).join(" ");
  const yTicks = [0, 25, 50, 75, 100];

  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={WIDTH} height={HEIGHT} style={{ display: "block" }}>
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={PAD_L + W} y1={toY(t)} y2={toY(t)} stroke={T.border} strokeDasharray="3,3" strokeWidth={0.8} />
            <text x={PAD_L - 6} y={toY(t) + 4} textAnchor="end" fontSize={9} fill={T.muted}>{t}%</text>
          </g>
        ))}
        {(() => {
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const x = PAD_L + Math.min(W, Math.max(0, (daysBetween(data.start, today) / data.span) * W));
          if (x < PAD_L || x > PAD_L + W) return null;
          return (
            <g>
              <line x1={x} x2={x} y1={PAD_T} y2={PAD_T + H} stroke={T.amber} strokeWidth={1.5} strokeDasharray="4,2" />
              <text x={x + 3} y={PAD_T + 10} fontSize={9} fill={T.amber} fontWeight="700">TODAY</text>
            </g>
          );
        })()}
        <path d={plannedPath} fill="none" stroke={T.blue} strokeWidth={2} />
        {actualPath && <path d={actualPath} fill="none" stroke={T.teal} strokeWidth={2.5} />}
        {points.filter((_, i) => i % 4 === 0).map((p) => (
          <text key={p.x} x={p.x} y={HEIGHT - 6} textAnchor="middle" fontSize={9} fill={T.muted}>
            {p.d.toLocaleDateString("en-ZA", { month: "short", year: "2-digit" })}
          </text>
        ))}
        <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={PAD_T + H} stroke={T.border} strokeWidth={1} />
        <line x1={PAD_L} x2={PAD_L + W} y1={PAD_T + H} y2={PAD_T + H} stroke={T.border} strokeWidth={1} />
      </svg>
      <div style={{ display: "flex", gap: 20, marginTop: 8, fontSize: 11, color: T.mutedBright }}>
        {[["Planned", T.blue], ["Actual", T.teal], ["Today", T.amber]].map(([l, c]) => (
          <span key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ display: "inline-block", width: 14, height: 3, background: c, verticalAlign: "middle", borderRadius: 99 }} />{l}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FLOAT DISTRIBUTION
// ─────────────────────────────────────────────────────────────────────────────
function FloatDistribution({ enrichedActivities }) {
  const buckets = useMemo(() => {
    const b = { "Critical (0)": 0, "Near (1–5)": 0, "Medium (6–14)": 0, "Safe (15+)": 0 };
    enrichedActivities.forEach((a) => {
      const tf = Number(a.TF ?? 999);
      if (tf === 0) b["Critical (0)"]++; else if (tf <= 5) b["Near (1–5)"]++; else if (tf <= 14) b["Medium (6–14)"]++; else b["Safe (15+)"]++;
    });
    return b;
  }, [enrichedActivities]);
  const total = enrichedActivities.length || 1;
  const palette = { "Critical (0)": T.maroon, "Near (1–5)": T.red, "Medium (6–14)": T.amber, "Safe (15+)": T.teal };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Object.entries(buckets).map(([label, count]) => {
        const pct = Math.round((count / total) * 100);
        const color = palette[label];
        return (
          <div key={label}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.text }}>{label}</span>
              <span style={{ fontSize: 11, fontFamily: T.fontMono, color: color }}>{count} <span style={{ color: T.muted }}>({pct}%)</span></span>
            </div>
            <div style={{ height: 6, background: T.bg, borderRadius: 99, overflow: "hidden", border: `1px solid ${T.border}` }}>
              <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width 0.4s" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CRITICAL PATH SUMMARY
// ─────────────────────────────────────────────────────────────────────────────
function CriticalPathSummary({ enrichedActivities }) {
  const criticalChain = useMemo(() => enrichedActivities.filter((a) => a.isCPM).sort((a, b) => (a.ES ?? 0) - (b.ES ?? 0)), [enrichedActivities]);
  if (!criticalChain.length) return <EmptyState message="Add activities with valid dates and dependencies to calculate the Critical Path." />;

  return (
    <div>
      <div style={{ padding: "8px 14px", borderRadius: T.r8, background: T.maroonSoft, border: `1px solid ${T.redDim}`, marginBottom: 14, display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: T.maroon }}>🔴 CRITICAL PATH</span>
        <span style={{ fontSize: 12, color: T.mutedBright }}>{criticalChain.length} activit{criticalChain.length !== 1 ? "ies" : "y"} with zero total float — any delay extends the project end date</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
        {criticalChain.map((a, i) => (
          <React.Fragment key={a._id || a.id}>
            <div style={{ padding: "6px 10px", borderRadius: T.r8, background: T.maroonSoft, border: `1px solid ${T.redDim}`, minWidth: 80, textAlign: "center" }}>
              <div style={{ fontSize: 9, fontFamily: T.fontMono, color: T.maroon, opacity: 0.8, marginBottom: 2 }}>{a.activityId || "?"}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.maroon, lineHeight: 1.3 }}>{(a.activityName?.substring(0, 18) || "—")}{(a.activityName?.length > 18) ? "…" : ""}</div>
              <div style={{ fontSize: 9, fontFamily: T.fontMono, color: T.muted, marginTop: 2 }}>{a._dur}d</div>
            </div>
            {i < criticalChain.length - 1 && <span style={{ fontSize: 12, color: T.redDim }}>→</span>}
          </React.Fragment>
        ))}
      </div>
      <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: T.r8, background: T.bgSurface, border: `1px solid ${T.border}`, fontSize: 11, color: T.muted }}>
        <strong style={{ color: T.mutedBright }}>CPM:</strong> Forward pass gives ES/EF, backward pass gives LS/LF. Total Float = LF − EF. Zero float = critical. Free Float shows how much an activity can slip without delaying its immediate successor.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOOK-AHEAD
// ─────────────────────────────────────────────────────────────────────────────
function LookAhead({ activities }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const horizon = addDays(today, 28);

  const upcoming = useMemo(() => activities
    .filter((a) => {
      if (a.status === "Completed") return false;
      const start  = startOfDay(a.revisedStart  || a.plannedStart);
      const finish = startOfDay(a.revisedFinish || a.plannedFinish);
      if (!start && !finish) return false;
      return (start && start <= horizon) || (finish && finish >= today && finish <= horizon);
    })
    .sort((a, b) => {
      const da = startOfDay(a.revisedStart || a.plannedStart) || new Date(9e12);
      const db = startOfDay(b.revisedStart || b.plannedStart) || new Date(9e12);
      return da - db;
    })
    .slice(0, 20),
    [activities]);

  if (!upcoming.length) return <EmptyState message="No activities starting or finishing in the next 28 days." />;

  const weekKey = (a) => {
    const s = startOfDay(a.revisedStart || a.plannedStart) || today;
    const diff = Math.max(0, daysBetween(today, s));
    const weekStart = addDays(today, Math.floor(diff / 7) * 7);
    return `Week of ${weekStart.toLocaleDateString("en-ZA", { day: "2-digit", month: "short" })}`;
  };
  const grouped = {};
  upcoming.forEach((a) => { const k = weekKey(a); if (!grouped[k]) grouped[k] = []; grouped[k].push(a); });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Object.entries(grouped).map(([week, items]) => (
        <div key={week}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", color: T.blue, marginBottom: 6, textTransform: "uppercase", borderBottom: `1px solid ${T.blueSoft || T.border}`, paddingBottom: 4 }}>{week}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {items.map((a) => {
              const finish = startOfDay(a.revisedFinish || a.plannedFinish);
              const daysLeft = finish ? daysBetween(today, finish) : null;
              const isLate = isActivityDelayed(a);
              return (
                <div key={a.id} style={{ display: "grid", gridTemplateColumns: "52px 1fr auto auto auto", gap: 10, alignItems: "center", padding: "7px 12px", borderRadius: T.r8, background: isLate ? T.redSoft : a.isCPM ? T.maroonSoft : T.bgSurface, border: `1px solid ${isLate ? T.redDim : a.isCPM ? T.redDim : T.border}` }}>
                  <span style={{ fontSize: 10, fontFamily: T.fontMono, fontWeight: 700, color: a.isCPM ? T.maroon : T.blue }}>{a.activityId || "—"}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.activityName}</div>
                    <div style={{ fontSize: 10, color: T.muted }}>{fmtDateShort(a.revisedStart || a.plannedStart)} → {fmtDateShort(a.revisedFinish || a.plannedFinish)}{a.discipline ? ` · ${a.discipline}` : ""}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {a.isCPM && <Badge variant="critical" size="xs">CP</Badge>}
                    {isLate && <Badge variant="danger" size="xs">Late</Badge>}
                  </div>
                  {daysLeft !== null && (
                    <span style={{ fontSize: 10, fontWeight: 700, fontFamily: T.fontMono, color: daysLeft < 0 ? T.red : daysLeft <= 7 ? T.amber : T.muted, whiteSpace: "nowrap" }}>
                      {daysLeft < 0 ? `${Math.abs(daysLeft)}d over` : `${daysLeft}d left`}
                    </span>
                  )}
                  <div style={{ width: 60 }}>
                    <ProgressBar value={a.progress} status={a.status} showLabel={false} height={4} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FLOAT DETAIL TABLE
// ─────────────────────────────────────────────────────────────────────────────
function FloatDetailTable({ enrichedActivities }) {
  return (
    <div style={{ overflowY: "auto", maxHeight: 340 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead style={{ position: "sticky", top: 0 }}>
          <tr style={{ background: T.bg }}>
            {["WBS", "Activity", "TF", "FF", "ES", "EF", "LS", "LF"].map((h) => (
              <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: T.muted, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...enrichedActivities].sort((a, b) => (a.TF ?? 999) - (b.TF ?? 999)).map((a) => (
            <tr key={a._id || a.id} style={{ borderBottom: `1px solid ${T.border}`, background: a.isCPM ? T.maroonSoft : (a.TF <= 5 ? T.redSoft : "transparent") }}>
              <td style={{ padding: "5px 8px", fontFamily: T.fontMono, fontWeight: 700, fontSize: 10, color: a.isCPM ? T.maroon : T.blue }}>{a.activityId || "—"}</td>
              <td style={{ padding: "5px 8px", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: T.text }} title={a.activityName}>{a.activityName || "—"}</td>
              <td style={{ padding: "5px 8px" }}><FloatPill totalFloat={a.TF} /></td>
              <td style={{ padding: "5px 8px", color: T.muted, fontFamily: T.fontMono }}>{a.FF ?? "—"}</td>
              <td style={{ padding: "5px 8px", color: T.muted, fontFamily: T.fontMono }}>{a.ES ?? "—"}</td>
              <td style={{ padding: "5px 8px", color: T.muted, fontFamily: T.fontMono }}>{a.EF ?? "—"}</td>
              <td style={{ padding: "5px 8px", color: T.muted, fontFamily: T.fontMono }}>{a.LS ?? "—"}</td>
              <td style={{ padding: "5px 8px", color: T.muted, fontFamily: T.fontMono }}>{a.LF ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────────────────────────────────────
function EmptyState({ message }) {
  return (
    <div style={{ padding: "28px", textAlign: "center", color: T.muted, fontSize: 13, fontFamily: T.fontSans }}>
      {message}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE-IN DRAWER  —  replaces the old collapsible form
// ─────────────────────────────────────────────────────────────────────────────
function ActivityDrawer({ open, onClose, programmeForm, setProgrammeForm, editingActivityId, setEditingActivityId, onSave, activityOptions }) {
  if (!open) return null;
  const isEditing = Boolean(editingActivityId);

  const field = (key, label, helper, type = "text", required = false) => (
    <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ ...lbl }}>{label}{required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}</label>
      {helper && <span style={{ fontSize: 10, color: T.muted, marginTop: -2, marginBottom: 2 }}>{helper}</span>}
      <input style={inp} type={type} value={programmeForm[key] || ""}
        onChange={(e) => setProgrammeForm({ ...programmeForm, [key]: e.target.value })} />
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, backdropFilter: "blur(2px)" }} />
      {/* Drawer */}
      <div style={{
        position: "fixed", right: 0, top: 0, bottom: 0, width: 560,
        background: T.bgPanel, borderLeft: `1px solid ${T.border}`,
        zIndex: 201, overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: T.shadowLg,
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, background: T.bg, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: "0.02em" }}>
              {isEditing ? `Editing: ${programmeForm.activityId || "Activity"}` : "Add Programme Activity"}
            </div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>
              Predecessor links auto-cascade revised dates · CPM calculated automatically
            </div>
          </div>
          <Btn variant="subtle" size="sm" onClick={onClose}>✕ Close</Btn>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {/* Left col */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <SectionHeading>Identity</SectionHeading>
              {field("activityId",   "Activity ID",     "Unique programme identifier",       "text",   true)}
              {field("activityName", "Activity Name",    "Programme activity description",    "text",   true)}
              {field("discipline",   "Discipline",       "Civil, structural, electrical…",   "text")}
              {field("area",         "Area / Section",   "Work section or zone",             "text")}

              <SectionHeading>Schedule</SectionHeading>
              {field("durationDays", "Duration (days)",  "Planned duration in calendar days", "number")}
              {field("lagDays",      "Lag Days",         "Waiting days after predecessor",   "number")}

              <SectionHeading>Completion</SectionHeading>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={lbl}>Status</label>
                <select style={inp} value={programmeForm.status} onChange={(e) => setProgrammeForm({ ...programmeForm, status: e.target.value })}>
                  {["Not Started", "In Progress", "Delayed", "Completed"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={lbl}>Progress % (0–100)</label>
                <input style={inp} type="number" min="0" max="100" value={programmeForm.progress}
                  onChange={(e) => setProgrammeForm({ ...programmeForm, progress: e.target.value })} />
                <ProgressBar value={programmeForm.progress} status={programmeForm.status} />
              </div>

              <SectionHeading>Dependency</SectionHeading>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={lbl}>Dependency Type</label>
                <select style={inp} value={programmeForm.dependencyType} onChange={(e) => setProgrammeForm({ ...programmeForm, dependencyType: e.target.value })}>
                  <option value="FS">Finish to Start (FS)</option>
                </select>
              </div>
              {["predecessor", "successor"].map((key) => (
                <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={lbl}>{key === "predecessor" ? "Predecessor" : "Successor"}</label>
                  <select style={inp} value={programmeForm[key]} onChange={(e) => setProgrammeForm({ ...programmeForm, [key]: e.target.value })}>
                    <option value="">None</option>
                    {activityOptions.filter((o) => o.value !== programmeForm.activityId).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              ))}

              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "8px 10px", borderRadius: T.r8, border: `1px solid ${programmeForm.critical ? T.redDim : T.border}`, background: programmeForm.critical ? T.maroonSoft : T.bgSurface }}>
                  <input type="checkbox" checked={programmeForm.critical} onChange={(e) => setProgrammeForm({ ...programmeForm, critical: e.target.checked })} style={{ accentColor: T.maroon }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: programmeForm.critical ? T.maroon : T.mutedBright }}>
                    {programmeForm.critical ? "Marked critical (manual override)" : "Non-critical (use CPM auto-detect)"}
                  </span>
                </label>
              </div>
            </div>

            {/* Right col — dates */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <SectionHeading>Dates</SectionHeading>
              {[
                ["plannedStart",   "Planned Start",   "Current planned start date"],
                ["plannedFinish",  "Planned Finish",  "Current planned finish date"],
                ["baselineStart",  "Baseline Start",  "Original accepted baseline start"],
                ["baselineFinish", "Baseline Finish", "Original accepted baseline finish"],
                ["revisedStart",   "Revised Start",   "Auto-set by predecessor or manual revision"],
                ["revisedFinish",  "Revised Finish",  "Auto-set by predecessor or manual revision"],
                ["actualStart",    "Actual Start",    "From linked diary Started status or manual input"],
                ["actualFinish",   "Actual Finish",   "From linked diary Finished status or manual input"],
              ].map(([k, l, h]) => field(k, l, h, "date"))}

              <SectionHeading>Notes</SectionHeading>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={lbl}>Remarks</label>
                <textarea rows={5} style={{ ...inp, resize: "vertical", lineHeight: 1.6 }}
                  value={programmeForm.remarks}
                  onChange={(e) => setProgrammeForm({ ...programmeForm, remarks: e.target.value })} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.border}`, background: T.bg, display: "flex", gap: 10, flexShrink: 0 }}>
          <Btn type="button" onClick={onSave} variant="primary">{isEditing ? "Update Activity" : "Save Activity"}</Btn>
          {isEditing && (
            <Btn variant="secondary" onClick={() => { setProgrammeForm(emptyProgrammeForm); setEditingActivityId(null); onClose(); }}>
              Cancel Edit
            </Btn>
          )}
        </div>
      </div>
    </>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: T.muted, textTransform: "uppercase", borderBottom: `1px solid ${T.border}`, paddingBottom: 4, marginTop: 4 }}>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FILTER / SEARCH BAR  (compact inline strip)
// ─────────────────────────────────────────────────────────────────────────────
function FilterBar({ searchText, setSearchText, statusFilter, setStatusFilter, criticalFilter, setCriticalFilter, areaFilter, setAreaFilter, sortBy, setSortBy, areaOptions, filteredCount, totalCount }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 14px", background: T.bgPanel, borderBottom: `1px solid ${T.border}` }}>
      {/* Search */}
      <div style={{ position: "relative", flex: "0 0 220px" }}>
        <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: T.muted, fontSize: 12, pointerEvents: "none" }}>⌕</span>
        <input
          style={{ ...inp, paddingLeft: 26, fontSize: 12, background: T.bg, height: 32, padding: "0 10px 0 26px" }}
          type="text" value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search ID, name, discipline…"
        />
      </div>

      {/* Dropdowns */}
      {[
        { val: statusFilter, set: setStatusFilter, opts: [["All", "All Status"], ["Not Started", "Not Started"], ["In Progress", "In Progress"], ["Delayed", "Delayed"], ["Completed", "Completed"]] },
        { val: criticalFilter, set: setCriticalFilter, opts: [["All", "All Activities"], ["Critical", "CPM Critical"], ["Non-Critical", "Non-Critical"], ["Manual Critical", "Manual Critical"]] },
        { val: areaFilter, set: setAreaFilter, opts: [["All", "All Areas"], ...areaOptions.map((a) => [a, a])] },
        { val: sortBy, set: setSortBy, opts: [["plannedStart", "Sort: Start Date"], ["wbs", "Sort: WBS/ID"], ["float", "Sort: Float ↑"]] },
      ].map((sel, i) => (
        <select key={i} value={sel.val} onChange={(e) => sel.set(e.target.value)}
          style={{ ...inp, fontSize: 12, background: T.bg, height: 32, padding: "0 8px", flex: "0 0 150px" }}>
          {sel.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ))}

      {/* Clear */}
      {(searchText || statusFilter !== "All" || criticalFilter !== "All" || areaFilter !== "All") && (
        <Btn variant="subtle" size="xs" onClick={() => { setSearchText(""); setStatusFilter("All"); setCriticalFilter("All"); setAreaFilter("All"); }}>Clear</Btn>
      )}

      <span style={{ marginLeft: "auto", fontSize: 11, color: T.muted, fontFamily: T.fontMono, whiteSpace: "nowrap" }}>
        {filteredCount}<span style={{ color: T.border }}>/{totalCount}</span>
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY REGISTER TABLE
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// INLINE-EDIT PRIMITIVES
//   Each cell renders read-only text by default and swaps to an input on click.
//   Save fires on blur or Enter; Esc reverts.
// ─────────────────────────────────────────────────────────────────────────────
const cellBase = {
  padding: "6px 10px",
  whiteSpace: "nowrap",
  fontSize: 11,
  color: T.text,
  cursor: "text",
  position: "relative",
  borderRight: `1px solid ${T.border}`,
};
const cellInput = {
  width: "100%", boxSizing: "border-box",
  border: `1.5px solid ${T.blue}`,
  borderRadius: T.r4,
  padding: "4px 6px", fontSize: 11,
  color: T.text, background: T.bgSurface,
  fontFamily: T.fontSans, outline: "none",
  boxShadow: `0 0 0 2px ${T.blueSoft}`,
};

function InlineText({ value, onCommit, mono, fontWeight, color, align = "left", placeholder = "—", title, maxWidth, type = "text", min, max, step }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  function commit() {
    setEditing(false);
    if ((draft ?? "") !== (value ?? "")) onCommit(draft);
  }
  function cancel() { setDraft(value ?? ""); setEditing(false); }
  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        min={min} max={max} step={step}
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === "Escape") { cancel(); }
        }}
        style={{ ...cellInput, textAlign: align, fontFamily: mono ? T.fontMono : T.fontSans }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      title={title || (value || "")}
      style={{
        minHeight: 20, cursor: "text",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        maxWidth, textAlign: align,
        fontFamily: mono ? T.fontMono : T.fontSans,
        fontWeight: fontWeight || 400,
        color: color || T.text,
      }}
    >
      {value === "" || value === null || value === undefined ? <span style={{ color: T.muted }}>{placeholder}</span> : value}
    </div>
  );
}

function InlineDate({ value, onCommit, color, fontWeight, suffix }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  function commit() {
    setEditing(false);
    if ((draft ?? "") !== (value ?? "")) onCommit(draft);
  }
  if (editing) {
    return (
      <input
        autoFocus
        type="date"
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); }
        }}
        style={{ ...cellInput, fontFamily: T.fontMono }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      style={{
        minHeight: 20, cursor: "pointer",
        fontFamily: T.fontMono,
        fontWeight: fontWeight || 400,
        color: color || T.textDim,
      }}
    >
      {value ? fmtDateShort(value) : <span style={{ color: T.muted }}>—</span>}
      {suffix}
    </div>
  );
}

function InlineSelect({ value, options, onCommit, renderBadge }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <select
        autoFocus
        value={value ?? ""}
        onChange={(e) => { onCommit(e.target.value); setEditing(false); }}
        onBlur={() => setEditing(false)}
        style={{ ...cellInput, paddingRight: 18 }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  return (
    <div onClick={() => setEditing(true)} style={{ cursor: "pointer", minHeight: 20 }}>
      {renderBadge ? renderBadge(value) : (value || <span style={{ color: T.muted }}>—</span>)}
    </div>
  );
}

// Predecessor/Successor cell — comma-separated activity IDs with autocomplete
function InlinePredSucc({ value, onCommit, activityOptions, currentActivityId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [highlight, setHighlight] = useState(-1);
  useEffect(() => { setDraft(value ?? ""); }, [value]);

  function commit() {
    setEditing(false);
    setHighlight(-1);
    if ((draft ?? "") !== (value ?? "")) onCommit(draft);
  }

  const lastSep = Math.max(draft.lastIndexOf(","), draft.lastIndexOf(";"));
  const prefix = draft.slice(0, lastSep + 1);
  const currentToken = draft.slice(lastSep + 1).trim().toLowerCase();
  const suggestions = currentToken
    ? activityOptions
        .filter((o) => o.value !== currentActivityId)
        .filter((o) => o.value.toLowerCase().includes(currentToken))
        .slice(0, 6)
    : [];

  function applySuggestion(opt) {
    const next = `${prefix}${prefix && !prefix.endsWith(" ") ? " " : ""}${opt.value}`;
    setDraft(next);
    setHighlight(-1);
  }

  if (editing) {
    return (
      <div style={{ position: "relative" }}>
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setHighlight(-1); }}
          onBlur={() => setTimeout(commit, 120)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (highlight >= 0 && suggestions[highlight]) { e.preventDefault(); applySuggestion(suggestions[highlight]); }
              else { e.preventDefault(); e.currentTarget.blur(); }
            } else if (e.key === "Escape") {
              setDraft(value ?? ""); setEditing(false); setHighlight(-1);
            } else if (e.key === "ArrowDown") {
              e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault(); setHighlight((h) => Math.max(h - 1, -1));
            } else if (e.key === "Tab" && highlight >= 0 && suggestions[highlight]) {
              e.preventDefault(); applySuggestion(suggestions[highlight]);
            }
          }}
          placeholder="A100, A105"
          style={{ ...cellInput, fontFamily: T.fontMono, fontSize: 10 }}
        />
        {suggestions.length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 2,
            background: T.bgPanel, border: `1px solid ${T.borderBright}`, borderRadius: T.r6,
            boxShadow: T.shadowMd, zIndex: 50, maxHeight: 180, overflowY: "auto",
          }}>
            {suggestions.map((opt, idx) => (
              <div key={opt.value}
                onMouseDown={(e) => { e.preventDefault(); applySuggestion(opt); }}
                onMouseEnter={() => setHighlight(idx)}
                style={{
                  padding: "5px 9px", fontSize: 11, cursor: "pointer",
                  background: highlight === idx ? T.bgHover : "transparent",
                  color: T.text, fontFamily: T.fontMono,
                  borderBottom: idx < suggestions.length - 1 ? `1px solid ${T.border}` : "none",
                }}>
                <span style={{ color: T.blue, fontWeight: 700 }}>{opt.value}</span>
                <span style={{ color: T.muted, marginLeft: 6 }}>{opt.label.split(" — ").slice(1).join(" — ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const ids = parsePredecessorIds(value);
  return (
    <div onClick={() => setEditing(true)}
      title={value || ""}
      style={{
        cursor: "text", minHeight: 20, display: "flex", flexWrap: "wrap", gap: 3,
        fontFamily: T.fontMono, fontSize: 10,
      }}>
      {ids.length === 0
        ? <span style={{ color: T.muted }}>—</span>
        : ids.map((id) => (
            <span key={id} style={{
              padding: "1px 6px", borderRadius: 999, background: T.blueSoft,
              color: T.blue, border: `1px solid ${T.blueDim}`, fontWeight: 700,
            }}>{id}</span>
          ))}
    </div>
  );
}

function InlineProgress({ value, status, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "0");
  useEffect(() => { setDraft(value ?? "0"); }, [value]);
  function commit() {
    setEditing(false);
    const clamped = Math.min(100, Math.max(0, Number(draft) || 0));
    if (String(clamped) !== String(value ?? "0")) onCommit(String(clamped));
  }
  if (editing) {
    return (
      <input
        autoFocus type="number" min="0" max="100" step="5"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === "Escape") { setDraft(value ?? "0"); setEditing(false); }
        }}
        style={{ ...cellInput, fontFamily: T.fontMono, width: 60 }}
      />
    );
  }
  return (
    <div onClick={() => setEditing(true)} style={{ cursor: "pointer", minWidth: 90 }}>
      <ProgressBar value={value} status={status} height={4} />
    </div>
  );
}

function InlineCheckbox({ value, onCommit, color = T.orange }) {
  return (
    <input
      type="checkbox"
      checked={Boolean(value)}
      onChange={(e) => onCommit(e.target.checked)}
      style={{ accentColor: color, cursor: "pointer", width: 14, height: 14 }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY TABLE  —  fully inline-editable, MS-Project-style register
// ─────────────────────────────────────────────────────────────────────────────
const ALL_COLUMNS = [
  { key: "wbs",       label: "WBS",         width: 90,   sortKey: "wbs",          default: true  },
  { key: "name",      label: "Activity",    width: 240,                            default: true  },
  { key: "disc",      label: "Disc.",       width: 90,                             default: true  },
  { key: "area",      label: "Area",        width: 110,                            default: false },
  { key: "dur",       label: "Dur (d)",     width: 70,                             default: true  },
  { key: "ps",        label: "Plan Start",  width: 100,  sortKey: "plannedStart",  default: true  },
  { key: "pf",        label: "Plan Finish", width: 100,                            default: true  },
  { key: "progress",  label: "Progress",    width: 130,                            default: true  },
  { key: "status",    label: "Status",      width: 120,                            default: true  },
  { key: "pred",      label: "Predecessors",width: 150,                            default: true  },
  { key: "succ",      label: "Successors",  width: 150,                            default: true  },
  { key: "lag",       label: "Lag",         width: 60,                             default: false },
  { key: "cp",        label: "CP",          width: 60,                             default: true  },
  { key: "tf",        label: "TF",          width: 70,   sortKey: "float",         default: true  },
  { key: "rs",        label: "Rev Start",   width: 100,                            default: true  },
  { key: "rf",        label: "Rev Finish",  width: 100,                            default: true  },
  { key: "as",        label: "Act Start",   width: 100,                            default: false },
  { key: "af",        label: "Act Finish",  width: 100,                            default: false },
  { key: "diary",     label: "Diary Link",  width: 150,                            default: false },
  { key: "behind",    label: "Behind",      width: 80,                             default: true  },
  { key: "manCrit",   label: "Man. Crit.",  width: 80,                             default: false },
  { key: "remarks",   label: "Remarks",     width: 180,                            default: false },
  { key: "actions",   label: "",            width: 80,                             default: true  },
];

function ColumnPicker({ visibleCols, setVisibleCols }) {
  const [open, setOpen] = useState(false);
  function toggle(key) {
    setVisibleCols((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  }
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)}
        style={{
          padding: "5px 10px", background: T.bgSurface, border: `1px solid ${T.border}`,
          borderRadius: T.r6, color: T.textDim, fontSize: 11, fontWeight: 600, cursor: "pointer",
          fontFamily: T.fontSans,
        }}>
        ⋮ Columns ({visibleCols.length})
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 200,
            background: T.bgPanel, border: `1px solid ${T.borderBright}`, borderRadius: T.r8,
            boxShadow: T.shadowLg, minWidth: 200, maxHeight: 360, overflowY: "auto",
            padding: "4px 0",
          }}>
            {ALL_COLUMNS.filter((c) => c.key !== "actions").map((c) => (
              <label key={c.key} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 12px",
                fontSize: 11, color: T.text, cursor: "pointer", userSelect: "none",
              }}>
                <input type="checkbox" checked={visibleCols.includes(c.key)} onChange={() => toggle(c.key)}
                  style={{ accentColor: T.blue, cursor: "pointer" }} />
                {c.label || c.key}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ActivityTable({
  filteredActivities, sortBy, setSortBy, loading,
  onEdit, onDelete, onCellSave, activityOptions,
  visibleCols,
}) {
  const [hoverRow, setHoverRow] = useState(null);

  const [collapsedSummaryIds, setCollapsedSummaryIds] = useState(() => new Set());

  function getRowKey(row) {
    return String(row.id || row.activityId || "");
  }

  function toggleSummaryCollapse(row) {
    const key = getRowKey(row);
    if (!key) return;
    setCollapsedSummaryIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const visibleActivities = useMemo(() => {
    const hiddenLevels = [];

    return filteredActivities.filter((row) => {
      const level = getOutlineLevel(row);

      while (hiddenLevels.length && level <= hiddenLevels[hiddenLevels.length - 1]) {
        hiddenLevels.pop();
      }

      if (hiddenLevels.length) {
        if (row.isSummaryTask && collapsedSummaryIds.has(getRowKey(row))) {
          hiddenLevels.push(level);
        }
        return false;
      }

      if (row.isSummaryTask && collapsedSummaryIds.has(getRowKey(row))) {
        hiddenLevels.push(level);
      }

      return true;
    });
  }, [filteredActivities, collapsedSummaryIds]);
  const cols = ALL_COLUMNS.filter((c) => visibleCols.includes(c.key) || c.key === "actions");

  const STATUS_OPTS = [
    { value: "Not Started", label: "Not Started" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed",   label: "Completed"   },
    { value: "Delayed",     label: "Delayed"     },
    { value: "On Hold",     label: "On Hold"     },
  ];

  function renderCell(row, col) {
    const delayed = isActivityDelayed(row);
    const behind  = getDaysBehind(row);
    const isCPM   = Boolean(row.isCPM);
    const save = (field, val) => onCellSave(row, field, val);

    switch (col.key) {
      case "wbs":
        return (
          <InlineText
            value={row.activityId || ""}
            onCommit={(v) => save("activityId", v.trim())}
            mono fontWeight={800}
            color={isCPM ? T.maroon : T.blue}
          />
        );
case "name": {
  const outlineLevel = getOutlineLevel(row);
  const isSummaryTask = Boolean(row.isSummaryTask || Number(row.childCount || 0) > 0);
  const indentPx = Math.max(0, outlineLevel - 1) * 18;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: indentPx, minWidth: 0 }}>
      {isSummaryTask ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleSummaryCollapse(row); }}
          title={collapsedSummaryIds.has(getRowKey(row)) ? "Expand summary section" : "Collapse summary section"}
          style={{
            width: 18,
            height: 18,
            borderRadius: T.r4,
            border: `1px solid ${T.amberDim}`,
            background: T.amberSoft,
            color: T.amber,
            cursor: "pointer",
            fontSize: 10,
            fontWeight: 900,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {collapsedSummaryIds.has(getRowKey(row)) ? "▸" : "▾"}
        </button>
      ) : outlineLevel > 1 ? (
        <span style={{ color: T.muted, fontSize: 11, flexShrink: 0 }}>└</span>
      ) : (
        <span style={{ width: 18, flexShrink: 0 }} />
      )}

      {isSummaryTask && (
        <span
          title={`${row.childCount || 0} child task${Number(row.childCount || 0) === 1 ? "" : "s"} rolled up`}
          style={{
            fontSize: 9,
            fontWeight: 900,
            color: T.amber,
            background: T.amberSoft,
            border: `1px solid ${T.amberDim}`,
            borderRadius: 999,
            padding: "1px 5px",
            flexShrink: 0,
            fontFamily: T.fontSans,
          }}
        >
          Σ
        </span>
      )}

      <InlineText
        value={row.activityName || ""}
        onCommit={(v) => save("activityName", v)}
        fontWeight={isSummaryTask ? 800 : 600}
        color={isSummaryTask ? T.amber : T.text}
        maxWidth={Math.max(80, col.width - 18 - indentPx)}
      />
    </div>
  );
}
      case "disc":
        return <InlineText value={row.discipline || ""} onCommit={(v) => save("discipline", v)} color={T.textDim} />;
      case "area":
        return <InlineText value={row.area || ""} onCommit={(v) => save("area", v)} color={T.textDim} />;
      case "dur":
        return <InlineText value={row.durationDays || ""} onCommit={(v) => save("durationDays", v)} type="number" min={1} mono align="right" color={T.text} />;
      case "ps":
        return <InlineDate value={row.plannedStart} onCommit={(v) => save("plannedStart", v)} color={T.textDim} />;
      case "pf":
        return <InlineDate value={row.plannedFinish} onCommit={(v) => save("plannedFinish", v)} color={T.textDim} />;
      case "progress":
        return <InlineProgress value={row.progress} status={row.status} onCommit={(v) => save("progress", v)} />;
      case "status":
        return (
          <InlineSelect
            value={row.status || "Not Started"}
            options={STATUS_OPTS}
            onCommit={(v) => save("status", v)}
            renderBadge={(v) => <Badge variant={statusBadgeVariant(v)} size="xs">{v || "—"}</Badge>}
          />
        );
      case "pred":
        return (
          <InlinePredSucc
            value={row.predecessor || ""}
            onCommit={(v) => save("predecessor", v)}
            activityOptions={activityOptions}
            currentActivityId={row.activityId}
          />
        );
      case "succ":
        return (
          <InlinePredSucc
            value={row.successor || ""}
            onCommit={(v) => save("successor", v)}
            activityOptions={activityOptions}
            currentActivityId={row.activityId}
          />
        );
      case "lag":
        return <InlineText value={row.lagDays ?? "0"} onCommit={(v) => save("lagDays", v)} type="number" mono align="right" color={T.textDim} />;
      case "cp":
        return isCPM
          ? <Badge variant="critical" size="xs">CPM</Badge>
          : row.critical
            ? <Badge variant="orange" size="xs">MAN</Badge>
            : <span style={{ color: T.muted }}>—</span>;
      case "tf":
        return <FloatPill totalFloat={row.TF ?? null} />;
      case "rs":
        return <InlineDate value={row.revisedStart} onCommit={(v) => save("revisedStart", v)} color={delayed ? T.red : T.textDim} />;
      case "rf":
        return (
          <InlineDate
            value={row.revisedFinish}
            onCommit={(v) => save("revisedFinish", v)}
            color={delayed ? T.red : T.textDim}
            fontWeight={delayed ? 700 : 400}
            suffix={delayed ? <span style={{ marginLeft: 4, fontSize: 10 }}>⚠</span> : null}
          />
        );
      case "as":
        return <InlineDate value={row.actualStart} onCommit={(v) => save("actualStart", v)} color={row.actualStart ? T.teal : T.textDim} />;
      case "af":
        return <InlineDate value={row.actualFinish} onCommit={(v) => save("actualFinish", v)} color={row.actualFinish ? T.teal : T.textDim} />;
      case "diary":
        return (
          <div style={{ display: "grid", gap: 2, maxWidth: col.width - 12 }}>
            {row.lastDiaryId ? (
              <>
                <span style={{ fontFamily: T.fontMono, fontSize: 10, fontWeight: 800, color: T.blue }}>{row.lastDiaryId}</span>
                <span style={{ fontSize: 10, color: T.mutedBright }}>{row.lastDiaryDate || "—"} · {row.lastDiaryStatus || "—"}</span>
                {row.lastDiaryNote && <span style={{ fontSize: 10, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.lastDiaryNote}</span>}
              </>
            ) : (
              <span style={{ color: T.muted, fontSize: 11 }}>—</span>
            )}
          </div>
        );
      case "behind":
        return (
          <span style={{
            fontFamily: T.fontMono, fontSize: 11,
            fontWeight: behind > 0 ? 700 : 400,
            color: behind > 0 ? T.red : T.muted,
          }}>{behind > 0 ? `+${behind}d` : "—"}</span>
        );
      case "manCrit":
        return <InlineCheckbox value={row.critical} onCommit={(v) => save("critical", v)} color={T.orange} />;
      case "remarks":
        return <InlineText value={row.remarks || ""} onCommit={(v) => save("remarks", v)} color={T.textDim} maxWidth={col.width - 12} />;
      case "actions":
        return (
          <div style={{ display: "flex", gap: 4 }}>
            <Btn size="xs" variant="secondary" onClick={() => save("__outdent", true)} title="Outdent activity">←</Btn>
            <Btn size="xs" variant="secondary" onClick={() => save("__indent", true)} title="Indent activity under the activity above">→</Btn>
            <Btn size="xs" variant="ghost" onClick={() => onEdit(row)} title="Open full editor">⋯</Btn>
            <Btn size="xs" variant="danger" onClick={() => onDelete(row.id)}>✕</Btn>
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div style={{ overflowX: "auto", overflowY: "visible" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
        <colgroup>
          {cols.map((c) => <col key={c.key} style={{ width: c.width }} />)}
        </colgroup>
        <thead>
          <tr style={{ background: T.bg }}>
            {cols.map(({ key, label, sortKey }) => (
              <th key={key}
                onClick={sortKey ? () => setSortBy(sortKey) : undefined}
                style={{
                  padding: "7px 10px", textAlign: "left", whiteSpace: "nowrap",
                  fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em",
                  color: sortKey && sortBy === sortKey ? T.blue : T.muted,
                  borderBottom: `2px solid ${sortKey && sortBy === sortKey ? T.blue : T.border}`,
                  borderRight: `1px solid ${T.border}`,
                  cursor: sortKey ? "pointer" : "default", userSelect: "none",
                  position: "sticky", top: 0, background: T.bg, zIndex: 2,
                }}>
                {label}{sortKey && sortBy === sortKey ? " ↑" : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={cols.length} style={{ padding: 28, textAlign: "center", color: T.muted }}>Loading programme activities…</td></tr>
          ) : filteredActivities.length === 0 ? (
            <tr><td colSpan={cols.length} style={{ padding: 28, textAlign: "center", color: T.muted }}>No activities match the current filter.</td></tr>
          ) : visibleActivities.length === 0 ? (
            <tr><td colSpan={cols.length} style={{ padding: 28, textAlign: "center", color: T.muted }}>All matching child activities are hidden under collapsed summary sections.</td></tr>
          ) : visibleActivities.map((row) => {
            const delayed = isActivityDelayed(row);
            const isCPM   = Boolean(row.isCPM);
            const isHover = hoverRow === row.id;
            return (
              <tr key={row.id}
                onMouseEnter={() => setHoverRow(row.id)}
                onMouseLeave={() => setHoverRow(null)}
               style={{
  borderBottom: `1px solid ${T.border}`,
  background: isHover ? T.bgHover
    : row.isSummaryTask ? T.bgSurface
    : delayed ? T.redSoft
    : isCPM   ? T.maroonSoft
    : "transparent",
  borderLeft: row.isSummaryTask ? `3px solid ${T.amber}` : isCPM ? `3px solid ${T.maroon}` : row.critical ? `3px solid ${T.orange}` : "3px solid transparent",
  fontWeight: row.isSummaryTask ? 700 : 400,
  transition: "background 0.1s",
}}>
                {cols.map((col) => (
                  <td key={col.key} style={{ ...cellBase, padding: col.key === "actions" ? "6px 8px" : "4px 8px" }}>
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS PANEL  (always visible, horizontal tabs, compact)
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "gantt",     label: "Gantt" },
  { id: "scurve",    label: "S-Curve" },
  { id: "cp",        label: "Critical Path" },
  { id: "float",     label: "Float Analysis" },
  { id: "lookahead", label: "4-Week Look-Ahead" },
];

function AnalyticsPanel({ activeTab, setActiveTab, ganttChartData, filteredActivities, enrichedActivities }) {
  return (
    <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r12, marginBottom: 10, overflow: "hidden" }}>
      {/* Tab bar */}
      <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, background: T.bg, overflowX: "auto" }}>
        {TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "9px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              border: "none", background: "transparent", fontFamily: T.fontSans,
              color: activeTab === tab.id ? T.blue : T.muted,
              borderBottom: `2px solid ${activeTab === tab.id ? T.blue : "transparent"}`,
              whiteSpace: "nowrap", transition: "all 0.15s",
            }}>{tab.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: "16px 20px" }}>
        {activeTab === "gantt"     && <GanttChart ganttData={ganttChartData} />}
        {activeTab === "scurve"    && (
          <div>
            <p style={{ margin: "0 0 12px", fontSize: 11, color: T.muted }}>Cumulative planned vs actual progress. Actual below planned = behind schedule.</p>
            <SCurveChart activities={filteredActivities} />
          </div>
        )}
        {activeTab === "cp"        && <CriticalPathSummary enrichedActivities={enrichedActivities} />}
        {activeTab === "float"     && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 24 }}>
            <div>
              <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 700, color: T.textDim }}>Float Distribution</p>
              <FloatDistribution enrichedActivities={enrichedActivities} />
              <div style={{ marginTop: 14, padding: "9px 12px", borderRadius: T.r8, background: T.amberSoft, border: `1px solid ${T.amberDim}`, fontSize: 11, color: T.amber }}>
                <strong>PM Tip:</strong> Float is a shared resource — if consumed by one activity, it's gone for all downstream tasks.
              </div>
            </div>
            <div>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: T.textDim }}>Float Detail by Activity</p>
              <FloatDetailTable enrichedActivities={enrichedActivities} />
            </div>
          </div>
        )}
        {activeTab === "lookahead" && (
          <div>
            <p style={{ margin: "0 0 10px", fontSize: 11, color: T.muted }}>Activities starting or finishing within the next 28 days, sorted by start date.</p>
            <LookAhead activities={filteredActivities} />
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// VERSION SELECTOR  —  history dropdown with type badge + restore button
// ─────────────────────────────────────────────────────────────────────────────
function VersionSelector({ versions, activeVersionId, onSelect, onSetLive, loadingVersions }) {
  const [open, setOpen] = useState(false);
  const active = versions.find(v => v.id === activeVersionId);
  const ats = active ? (PROG_TYPE[active.programmeType] || PROG_TYPE.Working) : null;

  function fmtDT(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
      {ats && (
        <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999,
          background: ats.bg, color: ats.color, border: `1px solid ${ats.border}`,
          letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: T.fontSans, flexShrink: 0 }}>
          {ats.label}
        </span>
      )}
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 8, padding: "5px 12px",
        background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: T.r8,
        color: T.text, cursor: "pointer", fontFamily: T.fontSans, fontSize: 12, fontWeight: 600, minWidth: 270,
      }}>
        <span style={{ color: T.muted, fontSize: 11, flexShrink: 0 }}>Programme:</span>
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {loadingVersions ? "Loading…" : active ? `${active.label}` : "Current (unsaved)"}
        </span>
        {active && !loadingVersions && (
          <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono, flexShrink: 0 }}>
            {fmtDT(active.importedAt).split(",")[0]}
          </span>
        )}
        <span style={{ color: T.muted, fontSize: 10, flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 299 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 300,
            background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r12,
            boxShadow: T.shadowLg, minWidth: 440, maxHeight: 380, overflowY: "auto",
          }}>
            <div style={{ padding: "8px 14px", borderBottom: `1px solid ${T.border}`,
              fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: T.muted, textTransform: "uppercase" }}>
              Programme History — {versions.length} version{versions.length !== 1 ? "s" : ""}
            </div>
            {versions.length === 0 && (
              <div style={{ padding: "18px 14px", fontSize: 12, color: T.muted }}>
                No saved versions yet. Import a programme or click 💾 Save Version to create the first snapshot.
              </div>
            )}
            {versions.map(v => {
              const vts = PROG_TYPE[v.programmeType] || PROG_TYPE.Working;
              const isActive = v.id === activeVersionId;
              return (
                <div key={v.id}
                  onClick={() => { onSelect(v.id); setOpen(false); }}
                  style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px",
                    cursor: "pointer", borderBottom: `1px solid ${T.border}`,
                    background: isActive ? T.bgHover : "transparent", transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgHover}
                  onMouseLeave={e => e.currentTarget.style.background = isActive ? T.bgHover : "transparent"}>
                  <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, marginTop: 2,
                    background: vts.bg, color: vts.color, border: `1px solid ${vts.border}`, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {vts.label}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.label}</div>
                    <div style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono, marginTop: 2 }}>
                      {fmtDT(v.importedAt)} · {v.activityCount} activities · {v.source || "Manual"}
                    </div>
                    {v.notes && (
                      <div style={{ fontSize: 10, color: T.mutedBright, marginTop: 2,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {v.notes}
                      </div>
                    )}
                  </div>
                  {isActive && (
                    <span style={{ fontSize: 9, color: T.teal, fontWeight: 800, flexShrink: 0, marginTop: 3 }}>ACTIVE</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      {active && (
        <button onClick={onSetLive} title="Reload this version into the live register"
          style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: T.r8,
            padding: "4px 9px", fontSize: 11, color: T.mutedBright, cursor: "pointer", fontFamily: T.fontSans, flexShrink: 0 }}>
          ↺ Restore
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SAVE VERSION MODAL
// ─────────────────────────────────────────────────────────────────────────────
function SaveVersionModal({ onSave, onCancel, defaultLabel = "", source = "" }) {
  const [label, setLabel] = useState(defaultLabel);
  const [type,  setType]  = useState("Working");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  async function handleModalSave() {
    if (!label.trim() || saving) return;

    setSaving(true);
    setModalError("");

    try {
      await onSave({
        label: label.trim(),
        programmeType: type,
        notes: notes.trim(),
      });
    } catch (e) {
      const message = e?.code === "permission-denied"
        ? "Firestore blocked this programme version save. Update your Firestore rules to allow writes to programmeVersions and programme activities."
        : `Save version failed: ${e?.message || e}`;

      console.error("Save programme version failed", e);
      setModalError(message);
      alert(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div
        onClick={saving ? undefined : onCancel}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.72)",
          zIndex: 400,
          backdropFilter: "blur(3px)",
          cursor: saving ? "default" : "pointer",
        }}
      />

      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 401,
          background: T.bgPanel,
          border: `1px solid ${T.border}`,
          borderRadius: T.r16,
          padding: "24px 28px",
          width: 500,
          boxShadow: T.shadowLg,
          fontFamily: T.fontSans,
        }}
      >
        <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 800, color: T.text }}>
          Save Programme Version
        </h3>

        <p style={{ margin: "0 0 20px", fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
          This snapshot is timestamped and stored permanently — recall it any time from the version history dropdown.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={lbl}>Version Label *</label>
            <input
              style={inp}
              type="text"
              value={label}
              disabled={saving}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Rev 3 — Post-delay update, Tender programme, Week 14 update"
            />
          </div>

          <div>
            <label style={lbl}>Programme Type *</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {Object.entries(PROG_TYPE).map(([key, ts]) => (
                <label
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 9,
                    padding: "9px 12px",
                    borderRadius: T.r8,
                    cursor: saving ? "default" : "pointer",
                    border: `1px solid ${type === key ? ts.color : T.border}`,
                    background: type === key ? ts.bg : T.bgSurface,
                    transition: "all 0.15s",
                    opacity: saving ? 0.65 : 1,
                  }}
                >
                  <input
                    type="radio"
                    name="ptype"
                    value={key}
                    checked={type === key}
                    disabled={saving}
                    onChange={() => setType(key)}
                    style={{ accentColor: ts.color, marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: type === key ? ts.color : T.text }}>
                      {ts.label}
                    </div>
                    <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>
                      {ts.desc}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label style={lbl}>Notes (optional)</label>
            <textarea
              rows={2}
              disabled={saving}
              style={{ ...inp, resize: "vertical", lineHeight: 1.6 }}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Revision reason, EOT reference, site instruction number, meeting date…"
            />
          </div>
        </div>

        {modalError && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: T.r8,
              background: T.redSoft,
              border: `1px solid ${T.redDim}`,
              color: T.red,
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {modalError}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              background: T.bgSurface,
              border: `1px solid ${T.border}`,
              borderRadius: T.r8,
              padding: "7px 14px",
              fontSize: 12,
              color: T.text,
              cursor: saving ? "default" : "pointer",
              fontFamily: T.fontSans,
              opacity: saving ? 0.5 : 1,
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleModalSave}
            disabled={!label.trim() || saving}
            style={{
              background: label.trim() && !saving ? T.teal : T.bgSurface,
              border: `1px solid ${label.trim() && !saving ? T.teal : T.border}`,
              borderRadius: T.r8,
              padding: "7px 16px",
              fontSize: 12,
              fontWeight: 700,
              color: label.trim() && !saving ? "#fff" : T.muted,
              cursor: label.trim() && !saving ? "pointer" : "default",
              fontFamily: T.fontSans,
              opacity: label.trim() && !saving ? 1 : 0.5,
            }}
          >
            {saving ? "Saving…" : "💾 Save Version"}
          </button>
        </div>
      </div>
    </>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// CONFIRM MODAL  (generic — used for delete-all)
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, confirmVariant = "danger", onConfirm, onCancel, children }) {
  return (
    <>
      <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
        zIndex: 400, backdropFilter: "blur(3px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        zIndex: 401, background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16,
        padding: "24px 28px", width: 460, boxShadow: T.shadowLg, fontFamily: T.fontSans }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 800, color: T.text }}>{title}</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: T.muted, lineHeight: 1.6 }}>{message}</p>
        {children}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onCancel} style={{ background: T.bgSurface, border: `1px solid ${T.border}`,
            borderRadius: T.r8, padding: "7px 14px", fontSize: 12, color: T.text, cursor: "pointer", fontFamily: T.fontSans }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ background: confirmVariant === "danger" ? T.red : T.teal,
              border: "none", borderRadius: T.r8, padding: "7px 16px",
              fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: T.fontSans }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INTERACTIVE NON-WORKING-DAY CALENDAR
//   Shows 2 months side-by-side. Click a day to toggle it as a one-off
//   non-working day. Click-drag (or click-start, click-end) to mark a range
//   as a named non-working window (e.g. Builders' Break).
// ─────────────────────────────────────────────────────────────────────────────
const WINDOW_COLORS = [
  { bg: "#3D1F08", border: "#7C3B0E", fg: "#FB923C" }, // orange (default — builders' break)
  { bg: "#2A1010", border: "#7F1D1D", fg: "#F87171" }, // red
  { bg: "#0D2E25", border: "#064E3B", fg: "#10B981" }, // teal
  { bg: "#1E1635", border: "#3B2D6A", fg: "#A78BFA" }, // purple
  { bg: "#1A2A4A", border: "#1D3461", fg: "#3B82F6" }, // blue
];

function NonWorkingCalendar({ draft, setDraft }) {
  // Anchor month (first day shown). Default: today's month.
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [anchor, setAnchor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  // Drag state for range selection
  const [dragStart, setDragStart] = useState(null);   // ISO YYYY-MM-DD
  const [dragEnd,   setDragEnd]   = useState(null);
  const [dragging,  setDragging]  = useState(false);

  // Pending range awaiting a label
  const [pendingRange, setPendingRange] = useState(null); // { start, end }
  const [rangeLabel, setRangeLabel]     = useState("");

  // Pre-build blocked-date lookup
  const blockedSet = useMemo(() => {
    return buildBlockedDateSet({
      holidays: draft.holidays || [],
      nonWorkingWindows: draft.nonWorkingWindows || [],
    });
  }, [draft.holidays, draft.nonWorkingWindows]);

  // Map every blocked date → which window (if any) it belongs to, for colouring
  const windowByDate = useMemo(() => {
    const map = {};
    (draft.nonWorkingWindows || []).forEach((w, idx) => {
      if (!w.start || !w.end) return;
      const colorIdx = (w.colorIndex ?? idx) % WINDOW_COLORS.length;
      const s = startOfDay(w.start); const e = startOfDay(w.end);
      if (!s || !e || e < s) return;
      let d = new Date(s);
      while (d <= e) {
        map[formatDateInput(d)] = { ...w, colorIdx };
        d = addDays(d, 1);
      }
    });
    return map;
  }, [draft.nonWorkingWindows]);

  function isHolidayOnly(iso) {
    return (draft.holidays || []).includes(iso) && !windowByDate[iso];
  }
  function isWeekend(date) {
    const dow = date.getDay();
    if (draft.calendarMode === "mon-fri") return dow === 0 || dow === 6;
    if (draft.calendarMode === "mon-sat") return dow === 0;
    return false;
  }

  function toggleHoliday(iso) {
    if (windowByDate[iso]) return; // can't toggle a day inside a named window
    const holidays = draft.holidays || [];
    const next = holidays.includes(iso)
      ? holidays.filter((d) => d !== iso)
      : [...holidays, iso].sort();
    setDraft({ ...draft, holidays: next });
  }

  function commitPendingRange(label) {
    if (!pendingRange) return;
    const colorIdx = (draft.nonWorkingWindows || []).length % WINDOW_COLORS.length;
    const newWindow = {
      id: `nw_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      label: label || `Non-working window`,
      start: pendingRange.start,
      end:   pendingRange.end,
      colorIndex: colorIdx,
    };
    setDraft({ ...draft, nonWorkingWindows: [...(draft.nonWorkingWindows || []), newWindow] });
    setPendingRange(null); setRangeLabel("");
  }

  function removeWindow(id) {
    setDraft({ ...draft, nonWorkingWindows: (draft.nonWorkingWindows || []).filter((w) => w.id !== id) });
  }

  function onDayMouseDown(iso, e) {
    if (windowByDate[iso]) return; // window days are non-interactive (click X on the window card to remove)
    e.preventDefault();
    setDragStart(iso); setDragEnd(iso); setDragging(true);
  }
  function onDayMouseEnter(iso) {
    if (dragging) setDragEnd(iso);
  }
  function onDayMouseUp(iso) {
    if (!dragging) return;
    setDragging(false);
    const start = dragStart < (dragEnd || iso) ? dragStart : (dragEnd || iso);
    const end   = dragStart < (dragEnd || iso) ? (dragEnd || iso) : dragStart;
    if (start === end) {
      // Single click → toggle as one-off holiday
      toggleHoliday(start);
    } else {
      // Range → ask for label
      setPendingRange({ start, end });
      // Default helpful label if it crosses mid-December (likely Builders' Break)
      const sd = new Date(start);
      const sm = sd.getMonth(); // 0=Jan, 11=Dec
      if (sm === 11) setRangeLabel(`Builders' Break ${sd.getFullYear()}`);
      else setRangeLabel("");
    }
    setDragStart(null); setDragEnd(null);
  }

  // Build a list of months to display: anchor + next month
  function getMonthMatrix(year, month) {
    const first = new Date(year, month, 1);
    const startDow = first.getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const rows = [];
    let day = 1 - startDow;
    while (day <= daysInMonth) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        if (day < 1 || day > daysInMonth) row.push(null);
        else row.push(new Date(year, month, day));
        day++;
      }
      rows.push(row);
    }
    return rows;
  }

  function renderMonth(monthDate) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const matrix = getMonthMatrix(year, month);
    const monthLabel = monthDate.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
    const dayHeaders = ["S", "M", "T", "W", "T", "F", "S"];

    return (
      <div key={`${year}-${month}`} style={{
        background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: T.r8,
        padding: 10, flex: 1, minWidth: 240,
      }}>
        <div style={{ textAlign: "center", fontSize: 12, fontWeight: 800, color: T.text, marginBottom: 8, letterSpacing: "0.02em" }}>
          {monthLabel}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {dayHeaders.map((h, i) => (
            <div key={`h${i}`} style={{
              textAlign: "center", fontSize: 9, fontWeight: 700, color: T.muted,
              padding: "2px 0", letterSpacing: "0.05em",
            }}>{h}</div>
          ))}
          {matrix.flat().map((d, idx) => {
            if (!d) return <div key={idx} style={{ height: 28 }} />;
            const iso = formatDateInput(d);
            const isWknd = isWeekend(d);
            const isHol  = isHolidayOnly(iso);
            const win    = windowByDate[iso];
            const isToday = iso === formatDateInput(today);

            // Active drag selection preview
            const inDragRange = dragging && dragStart && dragEnd && (
              (iso >= dragStart && iso <= dragEnd) || (iso >= dragEnd && iso <= dragStart)
            );

            let bg = "transparent";
            let fg = T.text;
            let border = "1px solid transparent";
            if (win) {
              const c = WINDOW_COLORS[win.colorIdx];
              bg = c.bg; fg = c.fg; border = `1px solid ${c.border}`;
            } else if (isHol) {
              bg = T.amberSoft; fg = T.amber; border = `1px solid ${T.amberDim}`;
            } else if (inDragRange) {
              bg = T.blueSoft; fg = T.blue; border = `1px dashed ${T.blue}`;
            } else if (isWknd) {
              bg = T.bg; fg = T.muted;
            }
            if (isToday && !win && !isHol) {
              border = `1px solid ${T.blue}`;
            }

            return (
              <div key={idx}
                onMouseDown={(e) => onDayMouseDown(iso, e)}
                onMouseEnter={() => onDayMouseEnter(iso)}
                onMouseUp={() => onDayMouseUp(iso)}
                title={win ? win.label : (isHol ? "Holiday — click to remove" : (isWknd ? "Weekend (non-working)" : iso))}
                style={{
                  height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontFamily: T.fontMono, fontWeight: 600,
                  cursor: win ? "not-allowed" : "pointer",
                  background: bg, color: fg, border, borderRadius: T.r4,
                  userSelect: "none",
                }}>
                {d.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function shiftAnchor(months) {
    setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + months, 1));
  }

  const nextMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);

  return (
    <div onMouseUp={() => { if (dragging) { setDragging(false); setDragStart(null); setDragEnd(null); } }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <Btn size="xs" variant="secondary" onClick={() => shiftAnchor(-12)} title="Previous year">«</Btn>
          <Btn size="xs" variant="secondary" onClick={() => shiftAnchor(-1)}  title="Previous month">‹</Btn>
          <Btn size="xs" variant="secondary" onClick={() => setAnchor(new Date(today.getFullYear(), today.getMonth(), 1))}>Today</Btn>
          <Btn size="xs" variant="secondary" onClick={() => shiftAnchor(1)}   title="Next month">›</Btn>
          <Btn size="xs" variant="secondary" onClick={() => shiftAnchor(12)}  title="Next year">»</Btn>
        </div>
        <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontSans }}>
          Click a day to toggle a holiday · Click-drag to mark a non-working range
        </span>
      </div>

      {/* Two months side by side */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        {renderMonth(anchor)}
        {renderMonth(nextMonth)}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, fontSize: 10, color: T.muted, fontFamily: T.fontSans }}>
        <LegendDot color={T.amber} bg={T.amberSoft} border={T.amberDim} label="One-off holiday" />
        <LegendDot color={WINDOW_COLORS[0].fg} bg={WINDOW_COLORS[0].bg} border={WINDOW_COLORS[0].border} label="Non-working window" />
        <LegendDot color={T.muted} bg={T.bg} border={T.border} label="Weekend (per calendar mode)" />
        <LegendDot color={T.blue} bg="transparent" border={T.blue} label="Today" />
      </div>

      {/* Pending range — ask for label */}
      {pendingRange && (
        <div style={{
          padding: "10px 12px", marginBottom: 10,
          background: T.blueSoft, border: `1px solid ${T.blueDim}`, borderRadius: T.r8,
        }}>
          <div style={{ fontSize: 11, color: T.blue, fontWeight: 700, marginBottom: 6 }}>
            New non-working window: {pendingRange.start} → {pendingRange.end}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="text"
              autoFocus
              value={rangeLabel}
              onChange={(e) => setRangeLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitPendingRange(rangeLabel || "Non-working window");
                else if (e.key === "Escape") { setPendingRange(null); setRangeLabel(""); }
              }}
              placeholder="e.g. Builders' Break 2026"
              style={{ ...inp, flex: 1 }}
            />
            <Btn size="sm" variant="primary" onClick={() => commitPendingRange(rangeLabel || "Non-working window")}>Add</Btn>
            <Btn size="sm" variant="secondary" onClick={() => { setPendingRange(null); setRangeLabel(""); }}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Defined non-working windows */}
      <div>
        <label style={{ ...lbl, marginBottom: 6 }}>Defined non-working windows</label>
        {(draft.nonWorkingWindows || []).length === 0 && (
          <p style={{ fontSize: 11, color: T.muted, margin: "4px 0" }}>
            None yet. Click-drag a range on the calendar above to define one (e.g. Builders' Break: 15 Dec – 9 Jan).
          </p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {(draft.nonWorkingWindows || []).map((w, idx) => {
            const c = WINDOW_COLORS[(w.colorIndex ?? idx) % WINDOW_COLORS.length];
            const days = (() => {
              const s = startOfDay(w.start), e = startOfDay(w.end);
              if (!s || !e) return 0;
              return daysBetween(s, e) + 1;
            })();
            return (
              <div key={w.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "7px 11px",
                background: c.bg, border: `1px solid ${c.border}`, borderRadius: T.r8,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: c.fg, fontFamily: T.fontSans }}>{w.label}</div>
                  <div style={{ fontSize: 10, color: T.textDim, fontFamily: T.fontMono, marginTop: 1 }}>
                    {w.start} → {w.end} · {days} calendar day{days === 1 ? "" : "s"}
                  </div>
                </div>
                <button onClick={() => removeWindow(w.id)} style={{
                  background: "transparent", border: "none", color: c.fg,
                  cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 4,
                }} title="Remove window">×</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, bg, border, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{
        width: 12, height: 12, borderRadius: T.r4,
        background: bg, border: `1px solid ${border}`,
      }} />
      <span style={{ color }}>{label}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROGRAMME SETTINGS MODAL
//   Tabbed: General (calendar mode + threshold) · Calendar (interactive picker)
// ─────────────────────────────────────────────────────────────────────────────
function ProgrammeSettingsModal({ settings, onSave, onClose }) {
  const [draft, setDraft] = useState(settings);
  const [activeTab, setActiveTab] = useState("general");
  const [newHoliday, setNewHoliday] = useState("");
  useEffect(() => { setDraft(settings); }, [settings]);

  function addHoliday() {
    if (!newHoliday) return;
    if ((draft.holidays || []).includes(newHoliday)) { setNewHoliday(""); return; }
    setDraft({ ...draft, holidays: [...(draft.holidays || []), newHoliday].sort() });
    setNewHoliday("");
  }
  function removeHoliday(d) {
    setDraft({ ...draft, holidays: (draft.holidays || []).filter((x) => x !== d) });
  }

  const CALENDAR_OPTIONS = [
    { value: "calendar", label: "Calendar days (7-day week)",       desc: "Every day counts. Use when the contract or work pattern is continuous." },
    { value: "mon-fri",  label: "Mon–Fri working days",              desc: "MS Project's Standard calendar. Weekends excluded." },
    { value: "mon-sat",  label: "Mon–Sat working days",              desc: "Typical SA construction. Only Sundays excluded." },
  ];

  const SETTINGS_TABS = [
    { id: "general",  label: "General" },
    { id: "calendar", label: "Calendar & Non-Working Days" },
  ];

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: 720, maxWidth: "94vw", maxHeight: "92vh",
        display: "flex", flexDirection: "column",
        background: T.bgPanel, border: `1px solid ${T.borderBright}`, borderRadius: T.r12,
        boxShadow: T.shadowLg, zIndex: 1001, color: T.text, fontFamily: T.fontSans,
      }}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: "0.02em" }}>Programme Settings</h3>
            <p style={{ margin: "3px 0 0", fontSize: 11, color: T.muted }}>Affects critical-path calculation and dependency dates.</p>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: T.muted, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }}>
          {SETTINGS_TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "10px 20px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                border: "none", background: "transparent", fontFamily: T.fontSans,
                color: activeTab === tab.id ? T.blue : T.muted,
                borderBottom: `2px solid ${activeTab === tab.id ? T.blue : "transparent"}`,
                transition: "all 0.15s",
              }}>{tab.label}</button>
          ))}
        </div>

        <div style={{ padding: "18px 22px", overflowY: "auto", flex: 1 }}>

          {activeTab === "general" && (
            <>
              {/* Calendar mode */}
              <label style={{ ...lbl, marginBottom: 8 }}>Schedule Calendar</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 18 }}>
                {CALENDAR_OPTIONS.map((opt) => (
                  <label key={opt.value} style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "10px 12px",
                    background: draft.calendarMode === opt.value ? T.blueSoft : T.bgSurface,
                    border: `1px solid ${draft.calendarMode === opt.value ? T.blue : T.border}`,
                    borderRadius: T.r8, cursor: "pointer",
                  }}>
                    <input type="radio"
                      checked={draft.calendarMode === opt.value}
                      onChange={() => setDraft({ ...draft, calendarMode: opt.value })}
                      style={{ accentColor: T.blue, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{opt.label}</div>
                      <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Critical threshold */}
              <label style={{ ...lbl, marginBottom: 6 }}>Critical-path slack threshold (days)</label>
              <input
                type="number" step="1"
                value={draft.criticalThreshold ?? 0}
                onChange={(e) => setDraft({ ...draft, criticalThreshold: Number(e.target.value) || 0 })}
                style={{ ...inp, width: 100, fontFamily: T.fontMono }}
              />
              <p style={{ margin: "6px 0 0", fontSize: 10, color: T.muted }}>
                Activities with Total Float ≤ this value are flagged as critical. MS Project default = 0.
              </p>
            </>
          )}

          {activeTab === "calendar" && (
            <>
              {/* Quick-add single holiday (kept for parity with old workflow) */}
              <label style={{ ...lbl, marginBottom: 6 }}>Quick-add single public holiday</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <input
                  type="date"
                  value={newHoliday}
                  onChange={(e) => setNewHoliday(e.target.value)}
                  style={{ ...inp, flex: 1, fontFamily: T.fontMono }}
                />
                <Btn size="sm" variant="secondary" onClick={addHoliday}>+ Add</Btn>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, minHeight: 24, marginBottom: 18 }}>
                {(draft.holidays || []).length === 0 && <span style={{ fontSize: 11, color: T.muted }}>No one-off holidays yet — or click days in the calendar below.</span>}
                {(draft.holidays || []).map((d) => (
                  <span key={d} style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "2px 5px 2px 8px", borderRadius: 999,
                    background: T.amberSoft, border: `1px solid ${T.amberDim}`,
                    fontSize: 10, color: T.amber, fontFamily: T.fontMono, fontWeight: 700,
                  }}>
                    {d}
                    <button onClick={() => removeHoliday(d)} style={{
                      background: "transparent", border: "none", color: T.amber,
                      cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0,
                    }}>×</button>
                  </span>
                ))}
              </div>

              <div style={{ height: 1, background: T.border, margin: "0 -22px 16px" }} />

              <label style={{ ...lbl, marginBottom: 8 }}>Interactive calendar</label>
              <NonWorkingCalendar draft={draft} setDraft={setDraft} />
            </>
          )}

        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 10, justifyContent: "flex-end", flexShrink: 0 }}>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={() => { onSave(draft); onClose(); }}>Save Settings</Btn>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function ProgrammeSection({ currentProject }) {
  const currentProjectId = currentProject?.id;

  // ── Core activity state ────────────────────────────────────────────────
  const [programmeForm,     setProgrammeForm]     = useState(emptyProgrammeForm);
  const [activities,        setActivities]        = useState([]);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [searchText,        setSearchText]        = useState("");
  const [statusFilter,      setStatusFilter]      = useState("All");
  const [criticalFilter,    setCriticalFilter]    = useState("All");
  const [areaFilter,        setAreaFilter]        = useState("All");
  const [sortBy,            setSortBy]            = useState("plannedStart");
  const [loading,           setLoading]           = useState(true);
  const [activeTab,         setActiveTab]         = useState("gantt");
  const [drawerOpen,        setDrawerOpen]        = useState(false);
  const [showAIImporter,    setShowAIImporter]    = useState(false);

  // ── Version history state ──────────────────────────────────────────────
  const [versions,          setVersions]          = useState([]);
  const [activeVersionId,   setActiveVersionId]   = useState(null);
  const [loadingVersions,   setLoadingVersions]   = useState(true);

  // ── Modal state ────────────────────────────────────────────────────────
  const [showSaveVersion,      setShowSaveVersion]      = useState(false);
  const [saveVersionSource,    setSaveVersionSource]    = useState("");
  const [showDeleteAll,        setShowDeleteAll]        = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");
  // payloads held here until user completes Save Version modal after import
  const [pendingImportPayloads, setPendingImportPayloads] = useState(null);

  // ── Programme settings (calendar + critical threshold) ─────────────────
  // Persisted on the project doc at projects/{id}.programmeSettings
  const [programmeSettings, setProgrammeSettings] = useState({
    calendarMode:      "mon-fri",   // "calendar" | "mon-fri" | "mon-sat"
    criticalThreshold: 0,           // TF <= threshold ⇒ critical (MS Project default = 0)
    holidays:          [],          // ["YYYY-MM-DD", ...] one-off non-working dates
    nonWorkingWindows: [],          // [{ id, label, start, end, color? }] named ranges (e.g. Builders' Break)
  });
  const [showSettings, setShowSettings] = useState(false);

  // ── Visible columns (persisted in localStorage per project) ────────────
  const colStorageKey = currentProjectId ? `progCols_${currentProjectId}` : "progCols_default";
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = typeof window !== "undefined" ? window.localStorage.getItem(colStorageKey) : null;
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch (e) { /* ignore */ }
    return ALL_COLUMNS.filter((c) => c.default && c.key !== "actions").map((c) => c.key);
  });
  useEffect(() => {
    try { window.localStorage.setItem(colStorageKey, JSON.stringify(visibleCols)); } catch (e) { /* ignore */ }
  }, [colStorageKey, visibleCols]);

  // ── Firestore: live activities ─────────────────────────────────────────
  useEffect(() => {
    if (!currentProjectId) { setActivities([]); setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "projects", currentProjectId, "programme"),
      (snap) => { setActivities(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); },
      (err)  => { console.error("Programme load error:", err); setLoading(false); }
    );
    return () => unsub();
  }, [currentProjectId]);

  // ── Firestore: version metadata ────────────────────────────────────────
  useEffect(() => {
    if (!currentProjectId) { setVersions([]); setLoadingVersions(false); return; }
    setLoadingVersions(true);
    const q = query(
      collection(db, "projects", currentProjectId, "programmeVersions"),
      orderBy("importedAt", "desc")
    );
    const unsub = onSnapshot(q,
      (snap) => { setVersions(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoadingVersions(false); },
      (err)  => { console.error("Versions load error:", err); setLoadingVersions(false); }
    );
    return () => unsub();
  }, [currentProjectId]);

  // ── Firestore: programme settings (on project doc) ─────────────────────
  useEffect(() => {
    if (!currentProjectId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "projects", currentProjectId));
        if (cancelled) return;
        const data = snap.data() || {};
        if (data.programmeSettings) {
          setProgrammeSettings((prev) => ({ ...prev, ...data.programmeSettings }));
        }
      } catch (err) {
        console.error("Programme settings load error:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentProjectId]);

  async function saveProgrammeSettings(next) {
    setProgrammeSettings(next);
    if (!currentProjectId) return;
    try {
      await setDoc(doc(db, "projects", currentProjectId), { programmeSettings: next }, { merge: true });
    } catch (err) {
      console.error("Programme settings save error:", err);
    }
  }

  // ── Derived data ────────────────────────────────────────────────────────
  const cpmCalendar = useMemo(() => ({
    mode:              programmeSettings.calendarMode || "calendar",
    holidays:          programmeSettings.holidays || [],
    nonWorkingWindows: programmeSettings.nonWorkingWindows || [],
  }), [programmeSettings.calendarMode, programmeSettings.holidays, programmeSettings.nonWorkingWindows]);

  const cpmOptions = useMemo(() => ({
    criticalThreshold: Number(programmeSettings.criticalThreshold ?? 0),
  }), [programmeSettings.criticalThreshold]);

 const hierarchyActivities = useMemo(() => applyHierarchyRollups(activities), [activities]);
const enrichedActivities  = useMemo(() => computeCPM(hierarchyActivities, cpmOptions), [hierarchyActivities, cpmOptions]);
const evm                 = useMemo(() => computeEVM(hierarchyActivities), [hierarchyActivities]);
  const activeVersion      = useMemo(() => versions.find(v => v.id === activeVersionId) || null, [versions, activeVersionId]);

  const activityOptions = useMemo(() =>
    [...activities].map((a) => ({ value: a.activityId || a.id, label: `${a.activityId || ""} — ${a.activityName || "Unnamed"}` }))
      .sort((a, b) => a.label.localeCompare(b.label)), [activities]);

  const areaOptions = useMemo(() => [...new Set(activities.map((a) => a.area).filter(Boolean))].sort(), [activities]);

  const filteredActivities = useMemo(() => {
  const cpmMap = {};
  enrichedActivities.forEach((a) => { cpmMap[a.activityId || a.id] = a; });
  return [...hierarchyActivities]
    .map((a) => ({ ...a, ...(cpmMap[a.activityId || a.id] || {}) }))
    .filter((row) => {
      if (statusFilter !== "All" && row.status !== statusFilter) return false;
      if (criticalFilter === "Critical"       && !row.isCPM)    return false;
      if (criticalFilter === "Non-Critical"   &&  row.isCPM)    return false;
      if (criticalFilter === "Manual Critical" && !row.critical) return false;
      if (areaFilter !== "All" && row.area !== areaFilter) return false;
      const hay = [row.activityId, row.activityName, row.discipline, row.area, row.predecessor, row.successor, row.remarks].join(" ").toLowerCase();
      return !searchText.trim() || hay.includes(searchText.toLowerCase());
    })
    .sort((a, b) => {
      if (sortBy === "wbs")   return (a.activityId || "").localeCompare(b.activityId || "", undefined, { numeric: true });
      if (sortBy === "float") return (a.TF ?? 999) - (b.TF ?? 999);
      if ((a.plannedStart || "") !== (b.plannedStart || "")) return (a.plannedStart || "").localeCompare(b.plannedStart || "");
      return (a.activityId || "").localeCompare(b.activityId || "");
    });
}, [hierarchyActivities, enrichedActivities, statusFilter, criticalFilter, areaFilter, searchText, sortBy]);

  const summary = useMemo(() => {
    const cpmCritical = enrichedActivities.filter((a) => a.isCPM).length;
    const delayed = activities.filter(isActivityDelayed);
    const critDelayed = delayed.filter((a) => { const e = enrichedActivities.find((x) => (x.activityId || x.id) === (a.activityId || a.id)); return e?.isCPM; }).length;
    return {
      total: activities.length, cpmCritical,
      manCritical: activities.filter((a) => Boolean(a.critical)).length,
      delayed: delayed.length, critDelayed,
      completed:   activities.filter((a) => a.status === "Completed").length,
      inProgress:  activities.filter((a) => a.status === "In Progress").length,
      notStarted:  activities.filter((a) => a.status === "Not Started").length,
      avgProgress: activities.length ? Math.round(activities.reduce((s, a) => s + Number(a.progress || 0), 0) / activities.length) : 0,
      nearCritical: enrichedActivities.filter((a) => !a.isCPM && Number(a.TF ?? 999) <= 5).length,
      criticalDelayed: critDelayed,
    };
  }, [activities, enrichedActivities]);

  const ganttChartData = useMemo(() => {
    if (!filteredActivities.length) return { chartStart: null, chartEnd: null, totalDays: 0, rows: [] };
    const cpmMap = {};
    enrichedActivities.forEach((a) => { cpmMap[a.activityId || a.id] = a; });
    const allDates = filteredActivities.flatMap((a) => [startOfDay(a.plannedStart), startOfDay(a.plannedFinish), startOfDay(a.revisedStart), startOfDay(a.revisedFinish), startOfDay(a.baselineStart), startOfDay(a.baselineFinish)]).filter(Boolean);
    if (!allDates.length) return { chartStart: null, chartEnd: null, totalDays: 0, rows: [] };
    const chartStart = new Date(Math.min(...allDates.map((d) => d.getTime())));
    const chartEnd   = new Date(Math.max(...allDates.map((d) => d.getTime())));
    const totalDays  = Math.max(1, daysBetween(chartStart, chartEnd) + 1);
    const rows = filteredActivities.map((a) => {
      const cpm = cpmMap[a.activityId || a.id] || {};
      const ps = startOfDay(a.plannedStart); const pf = startOfDay(a.plannedFinish);
      const rs = startOfDay(a.revisedStart);  const rf = startOfDay(a.revisedFinish);
      const bs = startOfDay(a.baselineStart); const bf = startOfDay(a.baselineFinish);
      const tf = cpm.TF ?? null;
      const plannedDur = ps && pf ? Math.max(1, daysBetween(ps, pf) + 1) : 0;
      return {
        id: a.id, wbs: a.activityId || "", label: a.activityName || "Unnamed Activity",
        isCritical: Boolean(cpm.isCPM), progress: Number(a.progress || 0), totalFloat: tf,
        plannedOffset: ps ? daysBetween(chartStart, ps) : 0, plannedDuration: plannedDur,
        floatDuration: tf !== null && tf > 0 ? tf : 0,
        revisedOffset: rs ? daysBetween(chartStart, rs) : 0, revisedDuration: rs && rf ? Math.max(1, daysBetween(rs, rf) + 1) : 0,
        baselineOffset: bs ? daysBetween(chartStart, bs) : 0, baselineDuration: bs && bf ? Math.max(1, daysBetween(bs, bf) + 1) : 0,
      };
    });
    return { chartStart, chartEnd, totalDays, rows };
  }, [filteredActivities, enrichedActivities]);

  // ── Version operations ─────────────────────────────────────────────────

  /** Write a batch of activities into the live programme collection,
      replacing everything that was there. */
  async function writeActivitiesToLive(newActivities) {
  if (!currentProjectId) {
    throw new Error("No active project selected.");
  }

  // Delete current live docs in batch
  const existing = await getDocs(collection(db, "projects", currentProjectId, "programme"));

  const deleteChunks = [];
  for (let i = 0; i < existing.docs.length; i += 490) {
    deleteChunks.push(existing.docs.slice(i, i + 490));
  }

  for (const chunk of deleteChunks) {
    const delBatch = writeBatch(db);
    chunk.forEach(d => delBatch.delete(d.ref));
    await delBatch.commit();
  }

  // Write new activities in chunks of 490
  const chunks = [];
  for (let i = 0; i < newActivities.length; i += 490) {
    chunks.push(newActivities.slice(i, i + 490));
  }

  for (const chunk of chunks) {
    const wb = writeBatch(db);

    chunk.forEach(a => {
      const docId = String(a.id || a.activityId || crypto.randomUUID()).replace(/[\/#?]/g, "-");

      wb.set(
        doc(db, "projects", currentProjectId, "programme", docId),
        { ...a, id: docId }
      );
    });

    await wb.commit();
  }
}
  /** Save a named snapshot of the current (or pending) activities. */
 async function handleSaveVersionConfirm({ label, programmeType, notes }) {
  if (!currentProjectId) {
    throw new Error("No active project selected.");
  }

  const toSnapshot = pendingImportPayloads || activities;

  if (!toSnapshot.length) {
    throw new Error("No programme activities available to save.");
  }

  try {
    const vRef = await addDoc(
      collection(db, "projects", currentProjectId, "programmeVersions"),
      {
        label,
        programmeType,
        notes: notes || "",
        importedAt: serverTimestamp(),
        activityCount: toSnapshot.length,
        source: saveVersionSource || "Manual",
      }
    );

    const chunks = [];
    for (let i = 0; i < toSnapshot.length; i += 490) {
      chunks.push(toSnapshot.slice(i, i + 490));
    }

    for (const chunk of chunks) {
      const wb = writeBatch(db);

      chunk.forEach(a => {
        const docId = String(a.id || a.activityId || crypto.randomUUID()).replace(/[\/#?]/g, "-");

        wb.set(
          doc(db, "projects", currentProjectId, "programmeVersions", vRef.id, "activities", docId),
          { ...a, id: docId }
        );
      });

      await wb.commit();
    }

    if (pendingImportPayloads) {
      await writeActivitiesToLive(pendingImportPayloads);
      setPendingImportPayloads(null);
    }

    setActiveVersionId(vRef.id);
    setShowSaveVersion(false);
    setSaveVersionSource("");
  } catch (e) {
    console.error("Programme version save failed:", e);
    throw e;
  }
}
  /** Load a historical version's activities into the live register. */
  async function handleSelectVersion(versionId) {
    if (!currentProjectId || !versionId) return;
    const snap = await getDocs(
      collection(db, "projects", currentProjectId, "programmeVersions", versionId, "activities")
    );
    const versionActivities = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    await writeActivitiesToLive(versionActivities);
    setActiveVersionId(versionId);
  }

  /** Delete ALL activities from the live register (versions are preserved). */
  async function handleDeleteAllConfirm() {
    if (!currentProjectId) return;
    const snap = await getDocs(collection(db, "projects", currentProjectId, "programme"));
    const wb = writeBatch(db);
    snap.docs.forEach(d => wb.delete(d.ref));
    await wb.commit();
    setActiveVersionId(null);
    setDeleteAllConfirmText("");
    setShowDeleteAll(false);
  }

  // ── Activity CRUD ──────────────────────────────────────────────────────

  // Propagate revised dates downstream from a changed activity.
  // Walks the successor graph until no more changes happen, respecting
  // multi-predecessor relationships and the configured calendar.
  function propagateDependencies(rootId, map) {
    const next = { ...map };
    const queue = [rootId];
    const visited = new Set();
    while (queue.length) {
      const cur = queue.shift();
      if (!cur || visited.has(cur)) continue;
      visited.add(cur);
      Object.keys(next).forEach((key) => {
        const a = next[key];
        const predIds = parsePredecessorIds(a.predecessor);
        if (predIds.includes(cur) || a.successor === cur) {
          const updated = applyDependencyToActivity(a, next, cpmCalendar);
          if (updated.revisedStart !== a.revisedStart || updated.revisedFinish !== a.revisedFinish) {
            next[key] = updated;
            queue.push(a.activityId || a.id);
          }
        }
      });
    }
    return next;
  }

  async function handleSaveActivity() {
    if (!currentProjectId || !programmeForm.activityId || !programmeForm.activityName) return;
    const activityDocId = editingActivityId || programmeForm.activityId;
    const payload = {
      ...programmeForm,
      activityId: programmeForm.activityId.trim(),
      activityName: programmeForm.activityName.trim(),
      predecessor: programmeForm.predecessor || "",
      successor: programmeForm.successor || "",
      dependencyType: programmeForm.dependencyType || "FS",
      lagDays: programmeForm.lagDays || "0",
      progress: programmeForm.progress || "0",
      id: activityDocId,
    };
    const nextActivityMap = {};
    activities.forEach((a) => { nextActivityMap[a.activityId || a.id] = { ...a }; });
    nextActivityMap[payload.activityId] = payload;
    // If a successor was named, ensure it lists this activity as a predecessor
    if (payload.successor && nextActivityMap[payload.successor]) {
      const succ = nextActivityMap[payload.successor];
      const succPreds = parsePredecessorIds(succ.predecessor);
      if (!succPreds.includes(payload.activityId)) {
        nextActivityMap[payload.successor] = {
          ...succ,
          predecessor: [...succPreds, payload.activityId].join(", "),
        };
      }
    }
    const propagated = propagateDependencies(payload.activityId, nextActivityMap);
    try {
      await Promise.all(Object.values(propagated).map((a) => {
        const docId = a.id || a.activityId;
        return setDoc(doc(db, "projects", currentProjectId, "programme", docId), { ...a, id: docId }, { merge: true });
      }));
      setActivities(Object.values(propagated));
      setProgrammeForm(emptyProgrammeForm);
      setEditingActivityId(null);
      setDrawerOpen(false);
      setActiveVersionId(null);
    } catch (err) { console.error("Programme save error:", err); }
  }

  // Inline-cell autosave. Writes a single field change for one activity,
  // recomputes downstream dates if the change is structural (dates / pred /
  // successor / duration / lag), and persists the affected docs.
  async function handleCellSave(row, field, value) {
    if (!currentProjectId || !row?.id) return;

    if (field === "__indent" || field === "__outdent") {
      const ordered = sortActivitiesByWbs(activities);
      const rowKey = row.id || row.activityId;
      const idx = ordered.findIndex((a) => (a.id || a.activityId) === rowKey);
      if (idx === -1) return;

      const currentLevel = getOutlineLevel(row);
      let nextLevel = currentLevel;

      if (field === "__indent") {
        if (idx === 0) return;
        const previous = ordered[idx - 1];
        const previousLevel = getOutlineLevel(previous);
        nextLevel = Math.min(previousLevel + 1, currentLevel + 1);
      }

      if (field === "__outdent") {
        nextLevel = Math.max(1, currentLevel - 1);
      }

      const draft = { ...row, outlineLevel: nextLevel };
      const parentActivityId = inferHierarchyParent(draft, ordered);

      const updatedActivities = activities.map((activity) => {
        if ((activity.id || activity.activityId) !== rowKey) return activity;
        return {
          ...activity,
          outlineLevel: nextLevel,
          parentActivityId,
        };
      });

      setActivities(updatedActivities);
      setActiveVersionId(null);

      try {
        await setDoc(
          doc(db, "projects", currentProjectId, "programme", row.id),
          {
            outlineLevel: nextLevel,
            parentActivityId,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (err) {
        console.error("Programme hierarchy save error:", err);
        setActivities(activities);
      }

      return;
    }

    const STRUCTURAL = new Set(["plannedStart", "plannedFinish", "durationDays", "predecessor", "successor", "lagDays", "revisedStart", "revisedFinish", "activityId"]);
    const isStructural = STRUCTURAL.has(field);

    // Build a mutable map keyed by activityId
    const map = {};
    activities.forEach((a) => { map[a.activityId || a.id] = { ...a }; });
    const lookupKey = row.activityId || row.id;
    const current = map[lookupKey];
    if (!current) return;

    // Normalize value per field
    let nextVal = value;
    if (field === "progress") nextVal = String(Math.min(100, Math.max(0, Number(value) || 0)));
    if (field === "durationDays" && value !== "") nextVal = String(Math.max(1, Number(value) || 1));
    if (field === "lagDays") nextVal = String(Number(value) || 0);
    if (field === "critical") nextVal = Boolean(value);

    const updated = { ...current, [field]: nextVal };
    map[lookupKey] = updated;

    // Auto-bookkeeping: if a successor was set, link the named successor back
    if (field === "successor" && nextVal && map[nextVal]) {
      const succ = map[nextVal];
      const succPreds = parsePredecessorIds(succ.predecessor);
      if (!succPreds.includes(lookupKey)) {
        map[nextVal] = { ...succ, predecessor: [...succPreds, lookupKey].join(", ") };
      }
    }

    // Propagate downstream dates if needed
    const propagated = isStructural ? propagateDependencies(lookupKey, map) : map;
    const changedDocs = Object.values(propagated).filter((a) => {
      const original = activities.find((x) => (x.activityId || x.id) === (a.activityId || a.id));
      if (!original) return true;
      return Object.keys(a).some((k) => a[k] !== original[k]);
    });

    // Optimistic UI update first
    setActivities(Object.values(propagated));
    setActiveVersionId(null);

    try {
      await Promise.all(changedDocs.map((a) => {
        const docId = a.id || a.activityId;
        return setDoc(doc(db, "projects", currentProjectId, "programme", docId), { ...a, id: docId }, { merge: true });
      }));
    } catch (err) {
      console.error("Inline cell save error:", err);
      // Revert on failure
      setActivities(activities);
    }
  }

  function handleEditActivity(row) {
    setProgrammeForm({
      activityId: row.activityId || "", activityName: row.activityName || "", discipline: row.discipline || "",
      area: row.area || "", plannedStart: row.plannedStart || "", plannedFinish: row.plannedFinish || "",
      durationDays: row.durationDays || "", progress: row.progress || "0", status: row.status || "Not Started",
      predecessor: row.predecessor || "", successor: row.successor || "", dependencyType: row.dependencyType || "FS",
      lagDays: row.lagDays || "0", critical: Boolean(row.critical), baselineStart: row.baselineStart || "",
      baselineFinish: row.baselineFinish || "", revisedStart: row.revisedStart || "", revisedFinish: row.revisedFinish || "", remarks: row.remarks || "",
    });
    setEditingActivityId(row.id);
    setDrawerOpen(true);
    setShowAIImporter(false);
  }

  async function handleDeleteActivity(id) {
    if (!currentProjectId) return;
    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "programme", id));
      if (editingActivityId === id) { setProgrammeForm(emptyProgrammeForm); setEditingActivityId(null); }
      setActiveVersionId(null);
    } catch (err) { console.error("Programme delete error:", err); }
  }

  /**
   * Called by ProgrammeAIImporter once the user clicks import.
   * We hold the payloads and show the Save Version modal so every
   * import is automatically timestamped and classified.
   */
  async function handleAIImportActivities(payloads, sourceLabel) {
    setPendingImportPayloads(payloads);
    setSaveVersionSource(sourceLabel || "Import");
    setShowAIImporter(false);
    setShowSaveVersion(true);
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: T.fontSans, background: T.bg, minHeight: "100vh", color: T.text }}>
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: ${T.blue} !important;
          box-shadow: 0 0 0 2px ${T.blueSoft};
        }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${T.bg}; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${T.borderBright}; }
      `}</style>

      {/* ── AI Importer overlay ───────────────────────────────────────── */}
      {showAIImporter && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, background: T.bg, overflowY: "auto", padding: "20px" }}>
          <ProgrammeAIImporter
            currentProjectId={currentProjectId}
            onImportActivities={handleAIImportActivities}
            onClose={() => setShowAIImporter(false)}
          />
        </div>
      )}

      {/* ── Save Version modal ───────────────────────────────────────────── */}
      {showSaveVersion && (
        <SaveVersionModal
          defaultLabel={
            saveVersionSource
              ? `${saveVersionSource} — ${new Date().toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}`
              : ""
          }
          source={saveVersionSource}
          onSave={handleSaveVersionConfirm}
          onCancel={async () => {
  try {
    if (pendingImportPayloads) {
      await writeActivitiesToLive(pendingImportPayloads);
      setPendingImportPayloads(null);
    }

    setShowSaveVersion(false);
    setSaveVersionSource("");
  } catch (e) {
    const message = e?.code === "permission-denied"
      ? "Firestore blocked the programme import. Update your Firestore rules to allow writes to this project's programme collection."
      : `Programme import failed: ${e?.message || e}`;

    console.error("Programme import on cancel failed:", e);
    alert(message);
  }
}}
        />
      )}

      {/* ── Delete-all confirm modal ─────────────────────────────────────── */}
      {showDeleteAll && (
        <ConfirmModal
          title="Clear All Programme Activities"
          message={`This will permanently delete all ${activities.length} activities from the live register. All saved versions are unaffected and can be reloaded at any time. Type DELETE to confirm.`}
          confirmLabel="Delete All Activities"
          confirmVariant="danger"
          onConfirm={() => deleteAllConfirmText === "DELETE" && handleDeleteAllConfirm()}
          onCancel={() => { setShowDeleteAll(false); setDeleteAllConfirmText(""); }}
        >
          <div>
            <label style={{ ...lbl, marginBottom: 6 }}>Type DELETE to confirm</label>
            <input
              style={{ ...inp, borderColor: deleteAllConfirmText === "DELETE" ? T.red : T.border }}
              type="text"
              value={deleteAllConfirmText}
              onChange={e => setDeleteAllConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            {deleteAllConfirmText.length > 0 && deleteAllConfirmText !== "DELETE" && (
              <p style={{ margin: "6px 0 0", fontSize: 11, color: T.red }}>Type exactly: DELETE</p>
            )}
          </div>
        </ConfirmModal>
      )}

      {/* ── Command Header / KPI Strip ───────────────────────────────────── */}
      <CommandHeader summary={summary} evm={evm} activeVersion={activeVersion} />

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div style={{ padding: "12px 16px" }}>

        <AlertStrip summary={summary} />

        {/* ── Version Control Toolbar ──────────────────────────────────── */}
        <div style={{
          display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
          marginBottom: 10, padding: "10px 14px",
          background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r12,
        }}>
          <VersionSelector
            versions={versions}
            activeVersionId={activeVersionId}
            onSelect={handleSelectVersion}
            onSetLive={() => activeVersionId && handleSelectVersion(activeVersionId)}
            loadingVersions={loadingVersions}
          />

          <div style={{ width: 1, height: 24, background: T.border, flexShrink: 0 }} />

          <Btn variant="teal" size="sm"
            disabled={activities.length === 0}
            onClick={() => { setSaveVersionSource("Manual save"); setShowSaveVersion(true); }}>
            💾 Save Version
          </Btn>

          <Btn variant="purple" size="sm" onClick={() => setShowAIImporter(true)}>
            🗂 Import Programme
          </Btn>

          <Btn variant="primary" size="sm"
            onClick={() => { setProgrammeForm(emptyProgrammeForm); setEditingActivityId(null); setDrawerOpen(true); }}>
            + Add Activity
          </Btn>

          <Btn variant="secondary" size="sm" onClick={() => setShowSettings(true)}>
            ⚙ Settings
          </Btn>

          {/* Unsaved indicator — shown when live register has no matching saved version */}
          {!activeVersionId && activities.length > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10,
              color: T.amber, fontWeight: 700, fontFamily: T.fontSans }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.amber,
                display: "inline-block", animation: "pulse 2s infinite" }} />
              Unsaved changes
            </span>
          )}

          {/* ⊗ Clear All — rightmost, destructive action */}
          <div style={{ marginLeft: "auto" }}>
            <Btn variant="subtle" size="sm"
              disabled={activities.length === 0}
              onClick={() => { setDeleteAllConfirmText(""); setShowDeleteAll(true); }}>
              <span style={{ color: T.red }}>⊗ Clear All</span>
            </Btn>
          </div>
        </div>

        {/* ── Analytics Panel ──────────────────────────────────────────── */}
        <AnalyticsPanel
          activeTab={activeTab} setActiveTab={setActiveTab}
          ganttChartData={ganttChartData}
          filteredActivities={filteredActivities}
          enrichedActivities={enrichedActivities}
        />

        {/* ── Activity Register ─────────────────────────────────────────── */}
        <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r12, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, background: T.bg,
            display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: T.text, letterSpacing: "0.02em" }}>
              Programme Activity Register
            </span>
            {activeVersion && (() => {
              const vts = PROG_TYPE[activeVersion.programmeType] || PROG_TYPE.Working;
              return (
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 999, fontWeight: 700,
                  background: vts.bg, color: vts.color, border: `1px solid ${vts.border}` }}>
                  {vts.label}
                </span>
              );
            })()}
            {/* Calendar mode badge — makes the CPM convention visible at a glance */}
            <span style={{
              fontSize: 10, padding: "2px 8px", borderRadius: 999, fontWeight: 700,
              background: T.bgSurface, color: T.mutedBright, border: `1px solid ${T.border}`,
              fontFamily: T.fontMono, cursor: "pointer",
            }}
              title="Click to change calendar mode"
              onClick={() => setShowSettings(true)}>
              📅 {programmeSettings.calendarMode === "calendar" ? "7-day"
                : programmeSettings.calendarMode === "mon-fri" ? "Mon–Fri"
                : "Mon–Sat"}
              {" · "}TF≤{programmeSettings.criticalThreshold}d
            </span>
            <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>
              Click any cell to edit · Enter saves · Esc cancels
            </span>
            <div style={{ marginLeft: "auto" }}>
              <ColumnPicker visibleCols={visibleCols} setVisibleCols={setVisibleCols} />
            </div>
          </div>

          <FilterBar
            searchText={searchText} setSearchText={setSearchText}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            criticalFilter={criticalFilter} setCriticalFilter={setCriticalFilter}
            areaFilter={areaFilter} setAreaFilter={setAreaFilter}
            sortBy={sortBy} setSortBy={setSortBy}
            areaOptions={areaOptions}
            filteredCount={filteredActivities.length}
            totalCount={activities.length}
          />

          <ActivityTable
            filteredActivities={filteredActivities}
            sortBy={sortBy} setSortBy={setSortBy}
            loading={loading}
            onEdit={handleEditActivity}
            onDelete={handleDeleteActivity}
            onCellSave={handleCellSave}
            activityOptions={activityOptions}
            visibleCols={visibleCols}
          />
        </div>
      </div>

      {/* ── Settings modal ──────────────────────────────────────────────── */}
      {showSettings && (
        <ProgrammeSettingsModal
          settings={programmeSettings}
          onSave={saveProgrammeSettings}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* ── Slide-in Drawer ───────────────────────────────────────────────── */}
      <ActivityDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); if (!editingActivityId) setProgrammeForm(emptyProgrammeForm); }}
        programmeForm={programmeForm}
        setProgrammeForm={setProgrammeForm}
        editingActivityId={editingActivityId}
        setEditingActivityId={setEditingActivityId}
        onSave={handleSaveActivity}
        activityOptions={activityOptions}
      />
    </div>
  );
}